/* ──────────────────────────────────────────────────────────
   Fundamentos de Data Engineering – Ejercicios Conceptuales
   Lecciones 100 % conceptuales, agnósticas de tecnología.
   Orden basado en el programa del bootcamp.
   ────────────────────────────────────────────────────────── */

export interface RankingOption {
  id: number;
  text: string;
}

export interface RankingData {
  question: string;
  options: RankingOption[];
  correctOrder: number[];
  explanation: string;
}

export interface DEExercise {
  id: number;
  level: string;
  title: string;
  description: string;
  theory: string;
  exercise: {
    heading: string;
    instructions: string[];
    hint?: string;
  };
  modelAnswer: string;
  ranking?: RankingData;
}

export const deExercises: DEExercise[] = [

  /* ╔══════════════════════════════════════════════════════════╗
     ║  SEMANA 1 — ¿Qué es Data Engineering? Pensar en datos  ║
     ╚══════════════════════════════════════════════════════════╝ */

  // ── 1 ──
  {
    id: 1,
    level: 'SEMANA 1',
    title: 'Lección 1: ¿Qué hace un Data Engineer?',
    description: 'El rol del Data Engineer, sus responsabilidades diarias y por qué es el rol más demandado del ecosistema de datos.',
    theory: `# ¿Qué hace un Data Engineer?

## Definición

Un **Data Engineer** diseña, construye y mantiene la infraestructura que permite a una organización **recolectar, almacenar, transformar y distribuir datos** de forma confiable y escalable.

Es la persona que construye los "cimientos" sobre los que todo el equipo de datos trabaja.

## Responsabilidades diarias

### 1. Diseñar y construir pipelines de datos
- Automatizar el movimiento de datos de A a B
- Garantizar que los datos lleguen completos, correctos y a tiempo
- Manejar errores, reintentos y alertas

### 2. Modelar datos
- Crear estructuras optimizadas para análisis
- Diseñar tablas de hechos y dimensiones
- Definir cómo se relacionan los datos entre sí

### 3. Garantizar calidad
- Validar datos en cada paso del pipeline
- Detectar y manejar nulos, duplicados, outliers
- Implementar tests de calidad automatizados

### 4. Automatizar y orquestar
- Programar pipelines para que corran solos
- Coordinar dependencias entre tareas
- Monitorear que todo funcione correctamente

### 5. Gobernanza
- Controlar quién accede a qué datos
- Documentar el linaje (de dónde viene cada dato)
- Mantener un catálogo de datos actualizado

## ¿Dónde trabaja un Data Engineer?

| Industria | Ejemplo de lo que hace |
|-----------|----------------------|
| **E-commerce** | Pipeline de ventas, recomendaciones, inventario |
| **Fintech** | Detección de fraude, reportes regulatorios |
| **Salud** | Historiales clínicos, análisis de tratamientos |
| **Logística** | Tracking de envíos, optimización de rutas |
| **Media** | Métricas de reproducciones, personalización |

## Demanda del mercado

- Es uno de los roles **más demandados** en tecnología
- Las empresas necesitan Data Engineers **antes** que Data Scientists
- Sin datos limpios y organizados, no hay machine learning ni analytics
- Salarios competitivos a nivel global, con alta demanda de trabajo remoto`,
    exercise: {
      heading: 'Ejercicio: El rol del Data Engineer',
      instructions: [
        '1. Describí con tus propias palabras qué hace un Data Engineer en su día a día.',
        '2. ¿Por qué una empresa necesita un Data Engineer antes que un Data Scientist?',
        '3. Nombrá 3 responsabilidades clave de un Data Engineer y dá un ejemplo concreto de cada una.',
        '4. Elegí una industria de la tabla y describí un pipeline de datos que necesitaría.',
      ],
      hint: 'Pensá en quién construye los cimientos (Engineer) vs quién analiza los resultados (Scientist).',
    },
    modelAnswer: `## Respuesta modelo

**1. Día a día de un Data Engineer:**
Un Data Engineer pasa su día diseñando y manteniendo pipelines que mueven datos desde las fuentes (APIs, bases de datos, archivos) hasta tablas limpias y organizadas que otros equipos pueden consumir. Monitorea que los pipelines corran sin errores, investiga cuando algo falla, optimiza consultas lentas y documenta los cambios.

**2. ¿Por qué se necesita un DE antes que un DS?**
Porque un Data Scientist necesita datos **limpios, estructurados y accesibles** para entrenar modelos. Sin un Data Engineer que construya los pipelines y las tablas, el Scientist termina dedicando el 80% de su tiempo limpiando datos en vez de modelar. El Data Engineer es el que hace posible que los datos existan en un formato utilizable.

**3. Tres responsabilidades con ejemplos:**
- **Diseñar pipelines**: automatizar la carga diaria de transacciones desde el sistema de pagos a la base de datos analítica
- **Garantizar calidad**: implementar validaciones que alerten si las ventas del día tienen un 50% menos de registros que el promedio (posible error en la fuente)
- **Modelar datos**: crear una tabla \`fact_ventas\` con dimensiones de cliente, producto y tiempo para que el equipo de BI pueda hacer dashboards

**4. Ejemplo: E-commerce**
Pipeline diario: extraer pedidos del API del sistema de e-commerce → limpiar datos (formatear fechas, validar montos, eliminar pedidos de prueba) → cargar en tabla Silver → agregar métricas como ventas por categoría por día en Gold → alimentar dashboard del equipo comercial.`,
  },

  // ── 2 ──
  {
    id: 2,
    level: 'SEMANA 1',
    title: 'Lección 2: Data Engineer vs otros roles de datos',
    description: 'Las diferencias entre Data Engineer, Data Scientist, Data Analyst y Analytics Engineer.',
    theory: `# Data Engineer vs otros roles de datos

## El equipo de datos

En una organización madura de datos, hay varios roles especializados. Cada uno tiene un foco distinto y se complementan entre sí.

## Comparación detallada

| Aspecto | Data Engineer | Data Scientist | Data Analyst | Analytics Engineer |
|---------|--------------|----------------|--------------|-------------------|
| **Foco principal** | Infraestructura y pipelines | Modelos predictivos y ML | Reportes e insights | Transformación y modelado |
| **Pregunta que responde** | ¿Cómo muevo y transformo los datos? | ¿Qué puedo predecir? | ¿Qué pasó y por qué? | ¿Cómo modelo los datos para análisis? |
| **Output típico** | Pipelines, tablas, APIs de datos | Modelos ML, experimentos, papers | Dashboards, reportes, presentaciones | Modelos de datos, métricas, documentación |
| **Herramientas comunes** | Orquestadores, procesamiento distribuido | Notebooks, frameworks ML | BI tools, hojas de cálculo | Herramientas de transformación |
| **Perfil técnico** | Ingeniería de software + datos | Estadística + programación | Negocio + SQL + visualización | SQL + modelado + negocio |

## ¿Cómo interactúan?

### Flujo típico de un equipo de datos:

\`\`\`
Data Engineer          Analytics Engineer       Data Analyst         Data Scientist
     │                       │                      │                     │
Construye pipelines    Modela y transforma     Analiza y reporta    Entrena modelos
     │                       │                      │                     │
 [Fuentes] → [Bronze] → [Silver] → [Gold] → [Dashboards]          [ML Models]
\`\`\`

### Ejemplo concreto en un e-commerce:

1. **Data Engineer**: construye el pipeline que extrae pedidos del API cada hora y los carga en Bronze
2. **Analytics Engineer**: transforma los datos crudos en tablas limpias (Silver) y crea el modelo dimensional (Gold)
3. **Data Analyst**: crea dashboards de ventas diarias, analiza tendencias, presenta insights al equipo comercial
4. **Data Scientist**: usa los datos de Gold para entrenar un modelo de recomendaciones de productos

## ¿Cuándo un rol absorbe a otro?

En empresas chicas, un solo rol puede hacer todo. A medida que la empresa crece, se especializa:

| Tamaño empresa | Quién hace qué |
|---------------|---------------|
| **Startup (1-5 personas)** | Un "Data Person" hace todo |
| **Empresa mediana (5-20)** | DE + DA separados |
| **Empresa grande (20+)** | DE + AE + DA + DS bien separados |
| **Enterprise (100+)** | Equipos especializados por dominio |`,
    exercise: {
      heading: 'Ejercicio: Roles de datos',
      instructions: [
        '1. Un gerente te dice: "Necesitamos predecir qué clientes van a dejar de comprar". ¿Qué rol/es necesita contratar? ¿Por qué?',
        '2. ¿Cuál es la diferencia principal entre un Data Engineer y un Analytics Engineer?',
        '3. En una startup de 3 personas, ¿qué habilidades debería tener la persona de datos?',
        '4. Describí cómo colaborarían los 4 roles para resolver: "Queremos un sistema de detección de fraude en tiempo real".',
      ],
      hint: 'Un DS necesita datos limpios → necesita un DE primero. Pensá en las dependencias entre roles.',
    },
    modelAnswer: `## Respuesta modelo

**1. Predecir churn de clientes:**
Necesita un **Data Scientist** para entrenar el modelo predictivo, pero **antes** necesita un **Data Engineer** que construya el pipeline que recolecte y limpie los datos de comportamiento de compra. Si no hay datos limpios, el DS no puede trabajar. También necesita un **Data Analyst** para validar que las predicciones tienen sentido desde el punto de vista del negocio.

**2. Data Engineer vs Analytics Engineer:**
El **Data Engineer** se enfoca en la **infraestructura**: mover datos entre sistemas, garantizar que lleguen completos y a tiempo, manejar escala y performance. El **Analytics Engineer** se enfoca en la **transformación y modelado**: tomar los datos que el DE puso en Silver y crear modelos dimensionales, métricas de negocio, y documentación. El DE piensa en "¿cómo lo muevo?", el AE piensa en "¿cómo lo modelo para que sea útil?".

**3. Data Person en startup:**
Necesita ser "full stack data": extraer datos de APIs y bases de datos (DE), limpiar y modelar (AE), crear dashboards y presentar insights al equipo (DA), y eventualmente explorar modelos simples de predicción (DS). La prioridad es DE + DA: tener datos limpios y dashboards útiles.

**4. Sistema de detección de fraude:**
- **Data Engineer**: construye el pipeline de streaming que captura transacciones en tiempo real, las carga en Bronze, y las mueve a Silver con validaciones de calidad
- **Analytics Engineer**: modela las tablas históricas de transacciones fraudulentas y legítimas, crea features para el modelo
- **Data Scientist**: entrena un modelo de clasificación (fraude sí/no) usando las features y datos históricos
- **Data Analyst**: monitorea las métricas del sistema (tasa de detección, falsos positivos), reporta resultados al equipo de riesgo`,
  },

  // ── 3 ──
  {
    id: 3,
    level: 'SEMANA 1',
    title: 'Lección 3: Pensar en datos',
    description: 'Cómo un Data Engineer piensa en los datos: fuentes, formatos, volumen, velocidad y veracidad.',
    theory: `# Pensar en datos

## El mindset del Data Engineer

Antes de escribir una línea de código, un Data Engineer necesita **pensar en los datos**:
- ¿De dónde vienen?
- ¿En qué formato están?
- ¿Con qué frecuencia se actualizan?
- ¿Qué volumen manejan?
- ¿Son confiables?

## Las 5 V's de los datos

| V | Significado | Pregunta clave | Ejemplo |
|---|------------|---------------|---------|
| **Volumen** | Cantidad de datos | ¿Cuántos registros? ¿Cuántos GB/TB? | 10 millones de transacciones por día |
| **Velocidad** | Frecuencia de actualización | ¿Cada cuánto llegan datos nuevos? | Tiempo real, cada hora, diario |
| **Variedad** | Diversidad de formatos | ¿JSON? ¿CSV? ¿Imágenes? ¿Logs? | APIs en JSON + archivos CSV + logs |
| **Veracidad** | Confiabilidad | ¿Los datos son correctos? ¿Completos? | Datos de encuestas vs datos de sistemas |
| **Valor** | Utilidad para el negocio | ¿Qué decisiones se toman con estos datos? | Predecir ventas, detectar fraude |

## Tipos de fuentes de datos

### Sistemas transaccionales (OLTP)
- Bases de datos que soportan las operaciones del día a día
- Ejemplo: base de datos de un e-commerce donde se registran pedidos
- **Características**: muchas escrituras pequeñas, datos normalizados

### APIs y servicios externos
- Datos que vienen de sistemas de terceros
- Ejemplo: API de Google Analytics, API del clima, API de pagos
- **Características**: datos en JSON/XML, rate limits, posibles caídas

### Archivos planos
- CSV, JSON, Parquet, Excel
- Ejemplo: archivo CSV del proveedor con actualización de precios
- **Características**: pueden estar mal formateados, con errores de encoding

### Eventos y logs
- Registros de actividad de sistemas y usuarios
- Ejemplo: logs de un servidor web, eventos de clicks en una app
- **Características**: alto volumen, semi-estructurados

### Streaming
- Datos que llegan continuamente en tiempo real
- Ejemplo: datos de sensores IoT, transacciones financieras
- **Características**: nunca "terminan", necesitan procesamiento continuo

## Formatos de datos

| Formato | Tipo | Legible por humanos | Eficiente | Cuándo usar |
|---------|------|-------------------|-----------|-------------|
| **CSV** | Texto | Sí | No | Intercambio simple, archivos pequeños |
| **JSON** | Texto | Sí | No | APIs, datos semi-estructurados |
| **Parquet** | Binario | No | Muy eficiente | Almacenamiento analítico, big data |
| **Avro** | Binario | No | Eficiente | Streaming, schema evolution |
| **ORC** | Binario | No | Muy eficiente | Consultas analíticas complejas |

### ¿Por qué importa el formato?
- **CSV de 1 GB** → puede tardar minutos en leerse
- **Parquet del mismo dato** → puede tardar **segundos** porque es columnar y comprimido
- Elegir el formato correcto puede significar la diferencia entre un pipeline de 5 minutos y uno de 2 horas`,
    exercise: {
      heading: 'Ejercicio: Pensar en datos',
      instructions: [
        '1. Un banco te contrata. ¿Qué preguntas le harías antes de diseñar cualquier pipeline? Nombrá al menos 5.',
        '2. Clasificá estas fuentes según su tipo (OLTP, API, archivo, evento, streaming): base de datos de clientes, webhook de pagos, archivo CSV de sucursales, sensor de temperatura, Google Analytics.',
        '3. ¿Por qué un archivo Parquet es más eficiente que un CSV para datos analíticos?',
        '4. Tenés una fuente que envía datos en JSON con campos que a veces existen y a veces no. ¿Qué V de las 5 V\'s está en riesgo? ¿Cómo lo manejarías?',
      ],
      hint: 'Siempre empezá por entender la fuente antes de pensar en la solución técnica.',
    },
    modelAnswer: `## Respuesta modelo

**1. Preguntas al banco:**
- ¿Cuántas transacciones procesan por día? (Volumen)
- ¿Necesitan datos en tiempo real o está bien diario? (Velocidad)
- ¿De cuántos sistemas distintos vienen los datos? (Variedad)
- ¿Qué decisiones se toman con estos datos? (Valor)
- ¿Tienen problemas actuales de calidad de datos? (Veracidad)
- ¿Hay regulaciones que cumplir? (ej: datos personales, retención)
- ¿Quiénes van a consumir los datos? (analistas, científicos, reportes regulatorios)

**2. Clasificación:**
| Fuente | Tipo |
|--------|------|
| Base de datos de clientes | OLTP |
| Webhook de pagos | Evento/Streaming |
| Archivo CSV de sucursales | Archivo plano |
| Sensor de temperatura | Streaming |
| Google Analytics | API externa |

**3. Parquet vs CSV:**
Parquet es **columnar** (almacena cada columna junta), lo que permite leer solo las columnas necesarias en vez de toda la fila. Además está **comprimido** (ocupa mucho menos espacio) y guarda **metadata del esquema** (tipos de datos, estadísticas). Un CSV obliga a leer todo el archivo siempre, no tiene tipos de datos, y no está comprimido.

**4. JSON con campos variables → Veracidad en riesgo:**
La **Variedad** y **Veracidad** están comprometidas. Para manejarlo: definir un esquema esperado, aceptar campos opcionales como NULL, loggear cuando aparecen campos inesperados, y usar schema evolution para agregar campos nuevos automáticamente sin romper el pipeline.`,
  },

  // ── 4 ──
  {
    id: 4,
    level: 'SEMANA 1',
    title: 'Lección 4: Arquitecturas de datos — Data Warehouse',
    description: 'Qué es un Data Warehouse, cómo funciona, cuándo usarlo y sus limitaciones.',
    theory: `# Data Warehouse

## ¿Qué es un Data Warehouse?

Es un almacén de datos centralizado, **estructurado y optimizado para consultas analíticas**. Los datos llegan ya transformados y organizados.

## Características

| Característica | Detalle |
|---------------|---------|
| **Esquema** | Definido de antemano (schema-on-write) |
| **Datos** | Estructurados, limpios, transformados |
| **Optimizado para** | Lecturas rápidas, consultas complejas |
| **Usuarios** | Analistas, equipos de BI, ejecutivos |
| **Costo** | Alto (almacenar datos transformados y estructurados) |

## ¿Cómo funciona?

\`\`\`
[Fuente A] ──┐
[Fuente B] ──┤── ETL ──→ [Data Warehouse] ──→ [Dashboards]
[Fuente C] ──┘           (datos limpios)       [Reportes]
\`\`\`

### Flujo típico:
1. Los datos se **extraen** de múltiples fuentes
2. Se **transforman** (limpian, formatean, agregan) fuera del warehouse
3. Se **cargan** en el warehouse ya listos para consulta

## Ventajas

- **Consultas muy rápidas**: optimizado para análisis
- **Datos confiables**: llegan validados y limpios
- **Esquema definido**: todos saben qué contiene cada tabla
- **Gobernanza clara**: permisos, roles, auditoría

## Limitaciones

- **Inflexible**: cambiar el esquema es costoso
- **Solo datos estructurados**: no acepta imágenes, logs, JSON anidado
- **Proceso ETL pesado**: transformar antes de cargar es lento
- **Costo de almacenamiento**: solo guarda datos transformados, si se necesita el dato crudo hay que ir a la fuente
- **No escala bien con Big Data**: volúmenes masivos se vuelven costosos

## Ejemplos conceptuales

### Caso: Cadena de retail
- El warehouse contiene tablas de ventas, clientes, productos, tiendas
- Cada noche se ejecuta un ETL que extrae datos del sistema POS
- Los analistas consultan "ventas por región del último mes" al instante
- Si un dato está mal, hay que volver a correr el ETL desde la fuente

### ¿Cuándo elegir un Data Warehouse?
- Datos **estructurados** y predecibles
- Equipo de BI que necesita **consultas rápidas**
- Volúmenes **medianos** (hasta decenas de TB)
- Necesidad de **gobernanza** estricta`,
    exercise: {
      heading: 'Ejercicio: Data Warehouse',
      instructions: [
        '1. Explicá con tus palabras qué es un Data Warehouse y para qué sirve.',
        '2. ¿Qué significa "schema-on-write"? ¿Cuál es la ventaja y la desventaja?',
        '3. Una empresa recibe archivos JSON con datos de sensores IoT (miles por segundo, formato variable). ¿Es un buen caso para un Data Warehouse? ¿Por qué?',
        '4. Nombrá 2 ventajas y 2 limitaciones de un Data Warehouse.',
      ],
      hint: 'Un Data Warehouse es como una biblioteca organizada: todo tiene su lugar, pero agregar una sección nueva es difícil.',
    },
    modelAnswer: `## Respuesta modelo

**1. Qué es un Data Warehouse:**
Es un almacén centralizado donde se guardan datos ya transformados y organizados, optimizado para que equipos de análisis puedan consultar información de forma rápida y confiable. Es como una biblioteca donde todos los libros están catalogados y ordenados.

**2. Schema-on-write:**
Significa que el **esquema (estructura) se define ANTES de escribir los datos**. Ventaja: los datos siempre tienen el formato correcto, las consultas son predecibles y rápidas. Desventaja: si la fuente cambia (agrega una columna), hay que modificar el ETL y el esquema antes de poder cargar los nuevos datos, lo cual es lento y costoso.

**3. Sensores IoT → NO es ideal para un Data Warehouse:**
No, porque: (a) los datos son semi-estructurados (JSON variable), y el warehouse necesita esquema fijo; (b) miles por segundo es un volumen altísimo para un warehouse tradicional; (c) el formato variable requiere flexibilidad que el warehouse no ofrece. Un Data Lake sería más apropiado.

**4. Ventajas y limitaciones:**
Ventajas:
- Consultas extremadamente rápidas para análisis
- Datos confiables y validados

Limitaciones:
- Solo acepta datos estructurados (no puede guardar JSONs anidados o imágenes)
- Cambiar el esquema es costoso y lento`,
  },

  // ── 5 ──
  {
    id: 5,
    level: 'SEMANA 1',
    title: 'Lección 5: Arquitecturas de datos — Data Lake',
    description: 'Qué es un Data Lake, cómo funciona, y el riesgo del "Data Swamp".',
    theory: `# Data Lake

## ¿Qué es un Data Lake?

Es un repositorio que almacena datos **en su formato original** (crudo), sin importar la estructura. Acepta cualquier tipo de dato.

## Características

| Característica | Detalle |
|---------------|---------|
| **Esquema** | Se define al momento de leer (schema-on-read) |
| **Datos** | Crudos, semi-estructurados, no estructurados |
| **Optimizado para** | Almacenamiento masivo y flexible |
| **Usuarios** | Data Engineers, Data Scientists |
| **Costo** | Bajo (almacenamiento en archivos) |

## ¿Cómo funciona?

\`\`\`
[Fuente A] ──┐                    ┌── Data Scientists (ML)
[Fuente B] ──┤── ELT ──→ [Lake]  ──┤── Data Engineers (pipelines)
[Fuente C] ──┘  (datos   (crudos)  └── Analistas (exploración)
                crudos)
\`\`\`

### Flujo típico:
1. Los datos se **cargan** en su formato original (JSON, CSV, Parquet, imágenes)
2. Se **transforman** cuando se necesitan consultar
3. Cada consumidor aplica las transformaciones que necesita

## Ventajas

- **Ultra flexible**: acepta cualquier formato y estructura
- **Económico**: almacenamiento en archivos es barato
- **Escala masiva**: puede manejar petabytes sin problema
- **Conserva datos originales**: siempre tenés el dato crudo
- **Schema-on-read**: no necesitás definir la estructura por adelantado

## El peligro: Data Swamp (Pantano de datos)

### ¿Qué es un Data Swamp?
Un Data Lake **sin gobernanza** se convierte en un pantano:

| Data Lake sano | Data Swamp |
|---------------|------------|
| Datos catalogados y documentados | Nadie sabe qué hay adentro |
| Metadata de cada archivo | Archivos sin nombre ni contexto |
| Estructura de carpetas organizada | Carpetas caóticas y duplicadas |
| Permisos y accesos controlados | Todos escriben donde quieren |
| Datos con dueño responsable | Datos huérfanos |

### ¿Cómo evitar el Data Swamp?
- **Catálogo de datos**: documentar qué es cada dataset
- **Convenciones de nomenclatura**: nombres consistentes para carpetas y archivos
- **Metadata**: agregar quién lo creó, cuándo, de dónde viene
- **Permisos**: controlar quién escribe y dónde
- **Limpieza periódica**: eliminar datos obsoletos

## ¿Cuándo elegir un Data Lake?

- Datos **variados** (estructurados + semi-estructurados + no estructurados)
- **Volúmenes masivos** (TB a PB)
- Necesidad de **conservar datos crudos**
- Equipo técnico que puede transformar al leer
- Presupuesto limitado para almacenamiento`,
    exercise: {
      heading: 'Ejercicio: Data Lake',
      instructions: [
        '1. ¿Qué significa "schema-on-read"? ¿En qué se diferencia de "schema-on-write"?',
        '2. Dá 3 señales de que un Data Lake se convirtió en un Data Swamp.',
        '3. Un hospital quiere guardar: historiales clínicos (PDF), resultados de laboratorio (CSV), imágenes de rayos X (DICOM), y notas médicas (texto libre). ¿Data Warehouse o Data Lake? Justificá.',
        '4. ¿Cómo organizarías las carpetas de un Data Lake para una empresa de delivery?',
      ],
      hint: 'El Data Lake es flexible pero necesita disciplina. Sin gobernanza, se vuelve inutilizable.',
    },
    modelAnswer: `## Respuesta modelo

**1. Schema-on-read:**
Significa que la **estructura de los datos se interpreta al momento de leerlos**, no al almacenarlos. Los datos se guardan tal cual llegan. La diferencia con schema-on-write (Data Warehouse) es que en este último, los datos deben cumplir con un esquema predefinido ANTES de guardarse. Schema-on-read es más flexible pero requiere que el consumidor sepa interpretar los datos.

**2. Señales de Data Swamp:**
- Nadie puede explicar qué contiene la carpeta \`datos_2024_v2_final_FINAL\`
- Un analista necesita datos de ventas pero no sabe en cuál de las 15 tablas con nombres similares están los datos correctos
- Hay archivos CSV de hace 3 años que nadie sabe quién los subió ni para qué sirven

**3. Hospital → Data Lake:**
**Data Lake**, porque: (a) los datos son de tipos muy variados: PDFs, CSVs, imágenes médicas, texto libre — un Data Warehouse solo aceptaría los CSV; (b) el volumen de imágenes médicas es masivo; (c) los Data Scientists necesitan acceder a las imágenes crudas para entrenar modelos de diagnóstico; (d) cada tipo de dato puede procesarse de forma diferente.

**4. Estructura de carpetas para empresa de delivery:**
\`\`\`
data-lake/
  raw/                    ← datos crudos
    orders/               ← pedidos
      2025/01/15/         ← particionado por fecha
    drivers/              ← conductores
    restaurants/          ← restaurantes
    tracking/             ← GPS y tracking
  processed/              ← datos limpios
    orders_clean/
    driver_metrics/
  aggregated/             ← métricas
    daily_kpis/
    monthly_reports/
\`\`\``,
  },

  // ── 6 ──
  {
    id: 6,
    level: 'SEMANA 1',
    title: 'Lección 6: Arquitecturas de datos — Lakehouse',
    description: 'El paradigma moderno que combina lo mejor del Data Warehouse y el Data Lake.',
    theory: `# Lakehouse

## ¿Qué es un Lakehouse?

Es una arquitectura que combina la **flexibilidad del Data Lake** con la **estructura y rendimiento del Data Warehouse**.

## La evolución

\`\`\`
Años 80-2000        2010-2020           2020-presente
[Data Warehouse] → [Data Lake]       → [Lakehouse]
 Estructurado       Flexible             Lo mejor de ambos
 Costoso            Barato               Equilibrado
 Solo tablas        Todo formato          Todo formato + estructura
 Rápido consultar   Lento consultar      Rápido consultar
\`\`\`

## ¿Cómo logra combinar ambos mundos?

### La clave: una capa de estructura sobre archivos

| Capa | Qué hace |
|------|---------|
| **Almacenamiento** | Archivos en formato abierto (como un Data Lake) |
| **Capa de metadata** | Transacciones ACID, esquema, versiones (como un Warehouse) |
| **Motor de consultas** | Consultas rápidas sobre los archivos |

### Resultado:
- Almacenás como un **Data Lake** (archivos, bajo costo, cualquier formato)
- Consultás como un **Data Warehouse** (rápido, estructurado, confiable)
- Tenés **transacciones ACID**, versionado, y optimizaciones

## Comparación definitiva

| Aspecto | Data Warehouse | Data Lake | Lakehouse |
|---------|---------------|-----------|-----------|
| **Datos** | Solo estructurados | Cualquier formato | Cualquier formato |
| **Esquema** | Schema-on-write | Schema-on-read | Ambos |
| **Transacciones ACID** | Sí | No | Sí |
| **Costo almacenamiento** | Alto | Bajo | Bajo |
| **Velocidad de consulta** | Muy rápida | Lenta | Rápida |
| **Time Travel** | Limitado | No | Sí |
| **ML / Data Science** | Difícil | Fácil | Fácil |
| **Gobernanza** | Fuerte | Débil | Fuerte |

## ETL vs ELT

### ETL (Extract → Transform → Load)
- Datos se **transforman antes** de llegar al destino
- El procesamiento ocurre **fuera** del almacén
- Propio del Data Warehouse tradicional

### ELT (Extract → Load → Transform)
- Datos llegan **crudos** y se transforman **dentro** del almacén
- Aprovecha la potencia de cómputo del destino
- Propio del Lakehouse moderno

| Aspecto | ETL | ELT |
|---------|-----|-----|
| **Orden** | Transformar → Cargar | Cargar → Transformar |
| **Dónde se transforma** | Fuera del almacén | Dentro del almacén |
| **Flexibilidad** | Baja (datos llegan ya transformados) | Alta (datos crudos disponibles) |
| **Caso ideal** | Data Warehouse | Lakehouse |

## Batch vs Streaming vs Micro-batch

| Patrón | Descripción | Latencia | Ejemplo |
|--------|------------|----------|---------|
| **Batch** | Procesa grandes volúmenes en intervalos | Minutos a horas | Reporte diario de ventas |
| **Micro-batch** | Lotes pequeños y frecuentes | Segundos a minutos | Dashboard cada 5 min |
| **Streaming** | Evento por evento en tiempo real | Milisegundos | Detección de fraude |`,
    exercise: {
      heading: 'Ejercicio: Lakehouse y patrones de procesamiento',
      instructions: [
        '1. Explicá por qué el Lakehouse se considera "lo mejor de ambos mundos".',
        '2. Completá: "ETL transforma ______ de cargar. ELT transforma ______ de cargar."',
        '3. Para cada caso, elegí la arquitectura ideal (Warehouse, Lake o Lakehouse) y justificá: (a) Banco con reportes regulatorios estrictos; (b) Startup de ML que procesa imágenes; (c) E-commerce que necesita análisis + ML.',
        '4. Para cada caso, elegí el patrón de procesamiento (batch, micro-batch, streaming): (a) Reporte mensual de RRHH; (b) Dashboard de ventas actualizado cada 10 min; (c) Alerta de fraude en tarjetas de crédito.',
      ],
      hint: 'El Lakehouse es la respuesta moderna para la mayoría de los casos. Pero los anteriores siguen teniendo sus nichos.',
    },
    modelAnswer: `## Respuesta modelo

**1. Lakehouse = lo mejor de ambos mundos:**
Porque almacena datos en **formato abierto y de bajo costo** (como un Data Lake) pero agrega una **capa de metadata** que provee transacciones ACID, esquemas, y optimizaciones (como un Data Warehouse). Esto permite guardar cualquier tipo de dato (flexibilidad del Lake) pero consultarlo de forma rápida y confiable (velocidad del Warehouse).

**2. Completar:**
"ETL transforma **antes** de cargar. ELT transforma **después** de cargar."

**3. Arquitectura ideal:**
| Caso | Arquitectura | Justificación |
|------|-------------|---------------|
| Banco regulatorio | **Data Warehouse** | Datos 100% estructurados, necesita gobernanza estricta, esquemas predefinidos, auditoría completa |
| Startup ML + imágenes | **Data Lake** | Necesita almacenar datos no estructurados (imágenes), volumen masivo, flexibilidad de formatos |
| E-commerce análisis + ML | **Lakehouse** | Necesita ambos: datos estructurados para dashboards de ventas + datos crudos para modelos de recomendación |

**4. Patrón de procesamiento:**
| Caso | Patrón | Justificación |
|------|--------|---------------|
| Reporte mensual RRHH | **Batch** | Frecuencia baja (mensual), volumen predecible, no requiere urgencia |
| Dashboard ventas cada 10 min | **Micro-batch** | Necesita datos recientes pero no al instante, balance entre frescura y costo |
| Alerta fraude tarjetas | **Streaming** | Cada milisegundo cuenta, necesita procesamiento en tiempo real |`,
  },

  /* ╔══════════════════════════════════════════════════════════╗
     ║  SEMANA 2 — Arquitectura Medallion                      ║
     ╚══════════════════════════════════════════════════════════╝ */

  // ── 7 ──
  {
    id: 7,
    level: 'SEMANA 2',
    title: 'Lección 7: Arquitectura Medallion — El concepto',
    description: 'Qué es Medallion, por qué se adoptó como estándar, y cómo organiza los datos en capas progresivas.',
    theory: `# Arquitectura Medallion — El concepto

## ¿Qué es la Arquitectura Medallion?

Es un patrón de diseño que organiza los datos en **tres capas progresivas de calidad**. Cada capa tiene un propósito claro y definido.

Fue popularizada por el ecosistema Lakehouse y hoy es **estándar de la industria**.

## ¿Por qué existe?

### El problema que resuelve:
Sin una estructura clara, los datos se vuelven un caos:
- Nadie sabe qué datos están limpios y cuáles no
- Un analista usa una tabla que otro ingeniero está modificando
- No hay forma de reprocesar si algo sale mal
- Cada persona transforma los datos a su manera

### La solución:
Separar los datos en **tres capas con responsabilidades claras**:

\`\`\`
 ┌──────────┐      ┌──────────┐      ┌──────────┐
 │  BRONZE  │ ───→ │  SILVER  │ ───→ │   GOLD   │
 │ (crudo)  │      │ (limpio) │      │(negocio) │
 └──────────┘      └──────────┘      └──────────┘
   Inmutable        Validado          Agregado
   Tal cual          Limpio          Para consumo
    llega          Estandarizado      optimizado
\`\`\`

## Los principios fundamentales

| Principio | Detalle |
|-----------|---------|
| **Separación de responsabilidades** | Cada capa hace UNA cosa y la hace bien |
| **Inmutabilidad de Bronze** | Los datos crudos NUNCA se modifican |
| **Progresión de calidad** | Cada capa mejora la calidad del dato |
| **Reprocesabilidad** | Si algo falla, se puede reprocesar desde Bronze |
| **Claridad de consumo** | Gold es la ÚNICA capa que consume el negocio |

## Analogía: la cocina de un restaurante

| Capa | En la cocina | En datos |
|------|-------------|---------|
| **Bronze** | La heladera con ingredientes sin procesar | Datos crudos tal cual llegan |
| **Silver** | La mesada de preparación (lavado, cortado, medido) | Datos limpios y validados |
| **Gold** | El plato terminado, listo para servir | Métricas y KPIs para el negocio |

## ¿Por qué NO hacer todo en una sola capa?

1. Si la fuente envía datos mal → no tenés el original para comparar
2. Si la limpieza tiene un bug → no podés reprocesar desde cero
3. Si un analista modifica datos → otros analistas ven datos inconsistentes
4. Si querés cambiar la lógica de limpieza → tenés que recargar desde la fuente`,
    exercise: {
      heading: 'Ejercicio: Concepto Medallion',
      instructions: [
        '1. Explicá con tus palabras qué es la Arquitectura Medallion y por qué se creó.',
        '2. ¿Por qué es importante que Bronze sea inmutable? Dá un ejemplo de qué pasaría si no lo fuera.',
        '3. ¿Qué capa consume el equipo de negocio? ¿Qué capa consume el Data Engineer para debuggear?',
        '4. Un equipo carga datos de la fuente directo a una tabla limpia (sin capas intermedias). ¿Qué riesgos tiene este enfoque?',
      ],
      hint: 'La clave de Medallion es poder volver atrás: si algo sale mal en Silver, siempre está Bronze.',
    },
    modelAnswer: `## Respuesta modelo

**1. Qué es Medallion:**
Es un patrón que organiza los datos en tres capas progresivas: Bronze (datos crudos), Silver (datos limpios) y Gold (datos de negocio). Se creó para resolver el caos de tener datos sin organización, donde nadie sabía qué estaba limpio y qué no, y no se podía reprocesar si algo fallaba.

**2. Inmutabilidad de Bronze:**
Si Bronze no fuera inmutable y alguien modificara los datos crudos: (a) no podríamos comparar qué envió la fuente vs qué procesamos; (b) si encontramos un bug en la limpieza de Silver, no tendríamos los datos originales para reprocesar; (c) si la fuente reporta un error, no podríamos verificar qué nos enviaron. Ejemplo: un CSV de ventas llega con fechas en formato incorrecto. Si lo "arreglamos" en Bronze, perdemos la evidencia del formato original.

**3. Quién consume qué:**
- **Equipo de negocio** → consume **Gold** (métricas, KPIs, dashboards)
- **Data Engineer para debug** → consume **Bronze** (datos originales para comparar) y **Silver** (datos limpios para verificar transformaciones)
- **Data Scientists** → consumen **Silver** (datos limpios para entrenar modelos)

**4. Riesgos de no usar capas:**
- Si la fuente envía datos corruptos, los datos limpios se contaminan y no hay backup
- No se puede reprocesar: si la lógica de limpieza tiene un bug, hay que volver a la fuente externa
- No hay auditoría: no se puede comparar "dato original" vs "dato transformado"
- Si el pipeline falla a mitad de camino, los datos quedan en un estado inconsistente`,
  },

  // ── 8 ──
  {
    id: 8,
    level: 'SEMANA 2',
    title: 'Lección 8: Capa Bronze — Datos crudos e inmutables',
    description: 'Todo sobre la capa Bronze: qué guarda, cómo se estructura, metadata y patrones de ingesta.',
    theory: `# Capa Bronze — Datos crudos e inmutables

## ¿Qué es Bronze?

La primera capa de la arquitectura Medallion. Almacena los datos **exactamente como llegan de la fuente**, sin ninguna modificación.

## Regla de oro

**NUNCA se modifican los datos de Bronze.**

Si un registro tiene un error de tipeo, se guarda con el error. Si un campo está vacío, se guarda vacío. Si el JSON viene mal formateado, se guarda así.

## ¿Qué se guarda en Bronze?

| Fuente | Qué llega a Bronze |
|--------|-------------------|
| API de pedidos | JSON crudo con todos los campos, incluso los innecesarios |
| Sistema de pagos | Logs de transacciones sin filtrar |
| Archivo del proveedor | CSV tal cual fue enviado |
| Sensores IoT | Eventos crudos con timestamps |
| Webhook | Payload completo del evento |

## Metadata obligatoria

A cada registro o archivo se le agrega información de contexto:

| Campo | Propósito | Ejemplo |
|-------|----------|---------|
| \`_ingested_at\` | Cuándo se cargó | 2025-02-15 06:00:00 UTC |
| \`_source\` | De dónde viene | api-pedidos-v2 |
| \`_batch_id\` | Identificador de la carga | batch-20250215-001 |
| \`_file_name\` | Nombre del archivo origen | ventas_20250215.csv |
| \`_schema_version\` | Versión del esquema | 3 |

### ¿Por qué agregar metadata?
- Para saber **cuándo** se cargó cada dato
- Para **reprocesar** un batch específico si falla
- Para **debuggear**: "los datos del 15 de febrero estaban mal, ¿de qué archivo vinieron?"
- Para **auditoría**: "¿cuándo fue la última carga exitosa?"

## Estructura de Bronze

### Organización por fuente y fecha:
\`\`\`
bronze/
  pedidos/
    year=2025/month=02/day=15/
      batch-001.parquet
      batch-002.parquet
    year=2025/month=02/day=16/
      batch-001.parquet
  pagos/
    year=2025/month=02/day=15/
      raw_transactions.json
  proveedores/
    catalogo_20250215.csv
\`\`\`

## Patrones de ingesta a Bronze

### Full Load
- Se copia **toda la tabla** de origen cada vez
- El dato anterior se conserva (nueva partición/archivo)
- Ideal para: tablas de referencia pequeñas

### Incremental Load
- Solo se copian **registros nuevos o modificados**
- Se usa una columna de control (fecha, ID)
- Ideal para: tablas grandes que crecen constantemente

### CDC (Change Data Capture)
- Captura **cada cambio** (insert, update, delete)
- Se basa en logs de la base de datos
- Ideal para: historial completo de cambios

| Patrón | Volumen transferido | Captura deletes | Complejidad | Caso ideal |
|--------|-------------------|-----------------|-------------|-----------|
| **Full Load** | Todo | Sí (implícito) | Baja | Tablas chicas |
| **Incremental** | Solo cambios | No | Media | Tablas grandes |
| **CDC** | Solo cambios | Sí | Alta | Historial completo |`,
    exercise: {
      heading: 'Ejercicio: Capa Bronze',
      instructions: [
        '1. Un archivo CSV llega con la columna "precio" como texto ("$1.500,00" en vez de 1500.00). ¿Lo corregís en Bronze? ¿Por qué?',
        '2. ¿Qué metadata agregarías a un batch de datos de transacciones bancarias? Nombrá al menos 5 campos.',
        '3. Diseñá la estructura de carpetas de Bronze para un sistema de e-commerce que recibe: pedidos (API), inventario (CSV diario) y reviews de clientes (webhooks).',
        '4. Tabla de países (195 registros, cambia 1 vez al año) vs tabla de transacciones (50M registros, crece 100K/día). ¿Qué patrón de ingesta usarías para cada una? Justificá.',
      ],
      hint: 'Bronze es un "museo de datos": todo se preserva en su estado original.',
    },
    modelAnswer: `## Respuesta modelo

**1. ¿Corregir precio en Bronze?**
**NO.** Bronze es inmutable. El precio se guarda como "$1.500,00" exactamente como llegó. La corrección (convertir a número 1500.00) se hace en la capa Silver. Si después descubrimos que la conversión tiene un bug, podemos volver a Bronze y ver el valor original.

**2. Metadata para transacciones bancarias:**
| Campo | Ejemplo |
|-------|---------|
| \`_ingested_at\` | 2025-02-15T06:00:00Z |
| \`_source\` | core-bancario-v3 |
| \`_batch_id\` | batch-20250215-txn-001 |
| \`_record_count\` | 45.832 |
| \`_schema_version\` | 2.1 |
| \`_environment\` | production |
| \`_checksum\` | md5 del archivo para verificar integridad |

**3. Estructura de carpetas:**
\`\`\`
bronze/
  orders/          ← pedidos desde API
    2025/02/15/
      batch-001.parquet
  inventory/       ← inventario CSV diario
    2025/02/15/
      inventario_20250215.csv
  reviews/         ← reviews por webhook
    2025/02/15/
      events-001.json
\`\`\`

**4. Patrones de ingesta:**
- **Países (195 registros)** → **Full Load**: es tan pequeña que copiar todo cada vez es trivial y te garantiza tener los datos completos sin lógica compleja.
- **Transacciones (50M + 100K/día)** → **Incremental Load**: usar la fecha o un ID incremental para traer solo los 100K nuevos registros. Full Load copiaría 50M cada día, lo cual es innecesario y costoso.`,
  },

  // ── 9 ──
  {
    id: 9,
    level: 'SEMANA 2',
    title: 'Lección 9: Capa Silver — Datos limpios y validados',
    description: 'Todo sobre la capa Silver: transformaciones típicas, validaciones, estandarización y buenas prácticas.',
    theory: `# Capa Silver — Datos limpios y validados

## ¿Qué es Silver?

La segunda capa de Medallion. Toma los datos crudos de Bronze y los **limpia, valida, estandariza y estructura**.

Silver es la **fuente de verdad** del sistema: datos confiables y listos para ser consumidos por cualquier equipo técnico.

## Transformaciones típicas de Bronze → Silver

### 1. Corrección de tipos de datos
| Bronze (crudo) | Silver (limpio) |
|---------------|-----------------|
| "2025-02-15" (texto) | 2025-02-15 (fecha) |
| "$1.500,00" (texto) | 1500.00 (decimal) |
| "true" (texto) | true (booleano) |
| "42" (texto) | 42 (entero) |

### 2. Tratamiento de nulos

| Estrategia | Cuándo usar | Ejemplo |
|-----------|-------------|---------|
| **Mantener NULL** | El campo es opcional | Teléfono secundario |
| **Valor por defecto** | Hay un valor razonable | País → "Desconocido" |
| **Derivar de otro campo** | Se puede inferir | Ciudad desde código postal |
| **Rechazar el registro** | El campo es obligatorio | Pedido sin monto |

### 3. Eliminación de duplicados
- Identificar registros duplicados por clave natural
- Mantener el más reciente o el más completo
- Loggear cuántos duplicados se eliminaron

### 4. Estandarización de formatos
| Antes | Después |
|-------|---------|
| "Bs As", "buenos aires", "CABA" | "Buenos Aires" |
| "15/02/2025", "Feb 15, 2025" | 2025-02-15 |
| "USD", "usd", "dólares" | "USD" |

### 5. Validaciones de negocio
- Monto de venta > 0
- Fecha de nacimiento no puede ser futura
- Email debe contener @
- Código de producto debe existir en el catálogo

## Registro de transformaciones

Siempre documentar QUÉ se hizo:

| Campo | Propósito |
|-------|----------|
| \`_cleaned_at\` | Cuándo se procesó |
| \`_source_batch_id\` | De qué batch de Bronze viene |
| \`_validations_passed\` | ¿Pasó todas las validaciones? |
| \`_quality_score\` | Puntaje de calidad (0-100) |

## Estructura de Silver

\`\`\`
silver/
  orders/               ← pedidos limpios
    year=2025/month=02/
  customers/            ← clientes estandarizados
  products/             ← productos validados
  _quality_logs/        ← registros de validación
    rejected_orders/    ← pedidos rechazados por validación
\`\`\`

## Datos rechazados

Los registros que **no pasan validación** no se pierden:
- Se guardan en una tabla de **rechazados** con el motivo
- Se investigan y se corrigen en la fuente
- Es un indicador de calidad de los datos de origen`,
    exercise: {
      heading: 'Ejercicio: Capa Silver',
      instructions: [
        '1. Tenés estos datos en Bronze. ¿Qué transformaciones harías para Silver?',
        '   - nombre: "  juan PÉREZ  "',
        '   - fecha: "15-02-2025"',
        '   - monto: "$3.200,50"',
        '   - email: "juan@mail"',
        '   - teléfono: NULL',
        '2. Un registro de venta tiene monto = -500. ¿Qué hacés? ¿Lo eliminás, lo corregís, o lo rechazás?',
        '3. ¿Qué información guardarías en la tabla de "registros rechazados"?',
        '4. Diseñá al menos 5 validaciones para una tabla Silver de "pedidos de un e-commerce".',
      ],
      hint: 'Silver transforma pero NUNCA inventa datos. Si no puede inferir un valor, usa NULL o rechaza el registro.',
    },
    modelAnswer: `## Respuesta modelo

**1. Transformaciones:**
| Campo | Bronze | Silver | Transformación |
|-------|--------|--------|---------------|
| nombre | "  juan PÉREZ  " | "Juan Pérez" | Trim + Title Case |
| fecha | "15-02-2025" | 2025-02-15 | Parseo DD-MM-YYYY → formato estándar |
| monto | "$3.200,50" | 3200.50 | Quitar $, reemplazar . por nada, , por . |
| email | "juan@mail" | RECHAZADO | No es un email válido (falta dominio) |
| teléfono | NULL | NULL | Se mantiene NULL (campo opcional) |

**2. Monto = -500:**
**Rechazar** el registro y moverlo a la tabla de rechazados. No eliminarlo (se perdería) ni corregirlo (no sabemos cuál era el valor correcto). Se loggea "monto negativo: -500" y se escala al equipo de la fuente para que investiguen.

**3. Tabla de rechazados:**
| Campo | Ejemplo |
|-------|---------|
| registro_original | JSON completo del registro |
| tabla_destino | silver.orders |
| motivo_rechazo | "monto negativo (-500)" |
| regla_violada | "monto > 0" |
| fecha_rechazo | 2025-02-15T06:05:00Z |
| batch_origen | batch-20250215-001 |

**4. Validaciones para pedidos:**
1. \`monto_total > 0\` — el monto no puede ser negativo ni cero
2. \`fecha_pedido <= fecha_actual\` — no pueden existir pedidos futuros
3. \`id_cliente IS NOT NULL\` — todo pedido debe tener un cliente
4. \`cantidad > 0 AND cantidad < 10000\` — cantidad razonable (no negativa ni absurda)
5. \`email LIKE '%@%.%'\` — email con formato válido`,
  },

  // ── 10 ──
  {
    id: 10,
    level: 'SEMANA 2',
    title: 'Lección 10: Capa Gold — Datos de negocio',
    description: 'Todo sobre la capa Gold: métricas, KPIs, modelado dimensional y tablas para consumo de negocio.',
    theory: `# Capa Gold — Datos de negocio

## ¿Qué es Gold?

La tercera y última capa de Medallion. Contiene datos **agregados, modelados y optimizados** para que el equipo de negocio los consuma directamente.

## ¿Quién consume Gold?

| Consumidor | Cómo lo usa |
|-----------|------------|
| **Analistas de BI** | Dashboards y reportes |
| **Ejecutivos** | KPIs y métricas de negocio |
| **Data Scientists** | Features para modelos ML |
| **Aplicaciones** | APIs que sirven datos a productos |

## ¿Qué contiene Gold?

### 1. Tablas de hechos (Facts)
- Cada fila = un **evento medible** (venta, click, envío)
- Contienen **métricas numéricas** (monto, cantidad, duración)
- Son las tablas más grandes

### 2. Tablas de dimensiones (Dimensions)
- Describen el **contexto** de los hechos (quién, qué, dónde, cuándo)
- Contienen atributos para filtrar y agrupar
- Son más chicas pero con más columnas

### 3. Tablas de KPIs y métricas
- Agregaciones precalculadas para dashboards
- Ejemplo: ventas diarias por categoría, ticket promedio por mes

## Silver → Gold: ¿Qué cambia?

| Aspecto | Silver | Gold |
|---------|--------|------|
| **Diseño** | Normalizado (sin redundancia) | Dimensional (Star/Snowflake) |
| **Datos** | A nivel de registro individual | Agregados y resumidos |
| **Optimización** | Para transformación | Para consulta rápida |
| **Consumidor** | Equipos técnicos | Equipos de negocio |

## Ejemplo completo: E-commerce

### De Silver a Gold:

**Silver** tiene tablas normalizadas:
- \`silver.orders\` — pedidos limpios
- \`silver.customers\` — clientes estandarizados
- \`silver.products\` — productos validados

**Gold** produce:
- \`gold.fact_ventas\` — cada línea de ticket con FK a dimensiones
- \`gold.dim_clientes\` — atributos del cliente (nombre, segmento, ciudad)
- \`gold.dim_productos\` — atributos del producto (nombre, categoría, precio)
- \`gold.dim_tiempo\` — atributos de fecha (día, mes, trimestre, feriado)
- \`gold.kpi_ventas_diarias\` — ventas totales por día, ticket promedio

### El dashboard final muestra:
\`\`\`
┌─────────────────────────────────────────┐
│  VENTAS DEL MES                         │
│  ████████████████████  $2.5M   (+12%)   │
│                                         │
│  Ticket promedio: $850                  │
│  Pedidos: 2.941                         │
│  Clientes nuevos: 347                   │
│                                         │
│  Top categorías:                        │
│  1. Electrónica    $890K                │
│  2. Ropa           $650K                │
│  3. Hogar          $480K                │
└─────────────────────────────────────────┘
\`\`\`

Toda esa información viene de **Gold**. El analista no necesita hacer JOINs complejos; las tablas ya están modeladas para responder estas preguntas.

## Buenas prácticas de Gold

- **Nombrá las tablas con prefijo**: \`fact_\`, \`dim_\`, \`kpi_\`, \`rpt_\`
- **Documentá cada columna**: qué significa, cómo se calcula
- **Optimizá para lectura**: particionamiento, clustering
- **Versioná los cambios**: si cambia la lógica de un KPI, documentá cuándo y por qué`,
    exercise: {
      heading: 'Ejercicio: Capa Gold',
      instructions: [
        '1. ¿Cuál es la diferencia principal entre Silver y Gold?',
        '2. Nombrá 3 tablas que crearías en Gold para un **banco**: una de hechos, una de dimensiones, una de KPIs.',
        '3. Un analista te pide: "Necesito saber las ventas por país del último trimestre". ¿De qué capa saca esa información? ¿Qué tablas necesita?',
        '4. ¿Por qué las tablas de Gold se precalculan en vez de calcularse al momento de la consulta?',
      ],
      hint: 'Gold es como el "menú del restaurante": el cliente no va a la cocina, consume lo que ya está preparado.',
    },
    modelAnswer: `## Respuesta modelo

**1. Diferencia Silver vs Gold:**
**Silver** contiene datos limpios y normalizados a nivel de registro individual (cada transacción, cada cliente). **Gold** contiene datos modelados dimensionalmente y agregados, optimizados para que el equipo de negocio los consulte rápido. Silver es para transformación y uso técnico; Gold es para consumo final y dashboards.

**2. Tablas Gold para un banco:**
| Tipo | Tabla | Contenido |
|------|-------|-----------|
| **Hechos** | \`fact_transacciones\` | Cada transacción: monto, tipo, fecha, cuenta origen/destino |
| **Dimensión** | \`dim_cuentas\` | Tipo de cuenta, titular, sucursal, fecha apertura, estado |
| **KPI** | \`kpi_morosidad_mensual\` | Tasa de morosidad por producto y mes, monto en mora, cantidad de clientes |

**3. Ventas por país del último trimestre:**
De la capa **Gold**. Necesita cruzar \`fact_ventas\` (para los montos) con \`dim_clientes\` (para el país) y \`dim_tiempo\` (para filtrar el último trimestre). Si existe una tabla precalculada \`kpi_ventas_por_pais_trimestre\`, puede consultarla directamente sin hacer JOINs.

**4. ¿Por qué precalcular?**
Porque las agregaciones sobre millones de registros son costosas y lentas. Si un dashboard tiene que calcular "ventas del mes" cada vez que alguien lo abre, recorrería millones de filas cada vez. Precalculando y guardando el resultado en Gold, la consulta es instantánea. Además, garantiza que todos los usuarios ven los **mismos números** (no hay diferencias por cómo cada uno hace su consulta).`,
  },

  // ── 11 ──
  {
    id: 11,
    level: 'SEMANA 2',
    title: 'Lección 11: Buenas prácticas, documentación y linaje',
    description: 'Convenciones de nomenclatura, documentación técnica y rastreo del linaje de datos.',
    theory: `# Buenas prácticas, documentación y linaje

## Convenciones de nomenclatura

Sin nombres claros, nadie entiende qué contiene cada tabla.

### Reglas recomendadas:

| Elemento | Convención | Ejemplo bueno | Ejemplo malo |
|----------|-----------|--------------|-------------|
| **Tablas de hechos** | \`fact_\` + sustantivo plural | \`fact_ventas\` | \`ventas_data_v2\` |
| **Dimensiones** | \`dim_\` + sustantivo plural | \`dim_clientes\` | \`clientes_final\` |
| **KPIs** | \`kpi_\` + métrica + periodicidad | \`kpi_ventas_diarias\` | \`resumen1\` |
| **Staging** | \`stg_\` + fuente | \`stg_api_pedidos\` | \`temp_tabla\` |
| **Columnas** | snake_case descriptivo | \`fecha_nacimiento\` | \`fn\`, \`col3\` |
| **Schemas** | Nombre de capa | \`bronze.pedidos\` | \`db1.t2\` |

### ¿Por qué importa?
- Cualquier persona nueva puede entender qué contiene una tabla con solo leer su nombre
- Los prefijos (\`fact_\`, \`dim_\`, \`kpi_\`) indican el tipo inmediatamente
- Se facilitan las búsquedas y la automatización

## Documentación técnica

### Documentar cada tabla:

| Campo | Descripción | Ejemplo |
|-------|------------|---------|
| **Nombre** | Nombre completo de la tabla | \`gold.fact_ventas\` |
| **Propósito** | Qué contiene y para qué sirve | "Cada línea de ticket de venta" |
| **Grano** | Qué representa cada fila | "Una venta de un producto" |
| **Fuente** | De dónde vienen los datos | "bronze.api_pedidos → silver.orders" |
| **Frecuencia** | Cada cuánto se actualiza | "Diaria a las 06:00 UTC" |
| **Owner** | Quién es responsable | "Equipo de Data Engineering" |
| **Columnas** | Lista con tipo y descripción | Ver tabla de columnas |

### Documentar cada pipeline:

| Campo | Descripción |
|-------|------------|
| **Nombre** | pipeline_ventas_diarias |
| **Objetivo** | Cargar ventas del día anterior en Gold |
| **Frecuencia** | Diaria 06:00 UTC |
| **Dependencias** | pipeline_ingesta_pedidos debe haber terminado |
| **SLA** | Debe completarse antes de las 08:00 UTC |
| **Error handling** | 3 reintentos, alerta a Slack si falla |
| **Contacto** | data-team@empresa.com |

## Linaje de datos (Data Lineage)

### ¿Qué es?
El rastreo del **recorrido de cada dato**: de dónde vino, qué transformaciones sufrió, y dónde terminó.

### ¿Por qué importa?

| Situación | Sin linaje | Con linaje |
|-----------|-----------|-----------|
| "Este número está mal" | No sé de dónde salió | Puedo rastrear: Gold ← Silver ← Bronze ← API |
| "La fuente cambió un campo" | No sé qué tablas se afectan | Sé exactamente qué pipeline y tablas impacta |
| "¿Quién usa esta tabla?" | No tengo idea | Sé qué dashboards y reportes dependen de ella |

### Ejemplo de linaje:
\`\`\`
[API Pedidos] → bronze.raw_orders → silver.orders → gold.fact_ventas → Dashboard "Ventas"
                                                   → gold.kpi_ventas_diarias → Reporte mensual
\`\`\`

## Catálogo de datos

Un **catálogo** es un inventario centralizado de todos los datos:
- Lista de tablas con descripción y owner
- Diccionario de datos (qué significa cada columna)
- Tags y clasificaciones (PII, financiero, público)
- Popularidad (qué tablas se consultan más)
- Calidad (porcentaje de datos válidos)`,
    exercise: {
      heading: 'Ejercicio: Documentación y linaje',
      instructions: [
        '1. Renombrá estas tablas con convenciones profesionales: `datos_clientes_v3`, `ventas_2025`, `resumen_mensual`, `temp_productos`.',
        '2. Escribí la documentación básica para una tabla `gold.kpi_ventas_diarias` (propósito, grano, fuente, frecuencia, columnas principales).',
        '3. Un gerente reporta que el KPI "ventas del mes" muestra un número raro. ¿Cómo usarías el linaje para investigar?',
        '4. ¿Por qué es importante documentar el "grano" de una tabla?',
      ],
      hint: 'La buena documentación es lo que diferencia un proyecto profesional de uno amateur.',
    },
    modelAnswer: `## Respuesta modelo

**1. Renombrar tablas:**
| Original | Renombrada | Justificación |
|----------|-----------|---------------|
| \`datos_clientes_v3\` | \`dim_clientes\` | Es una dimensión, sin versiones en el nombre |
| \`ventas_2025\` | \`fact_ventas\` | Es una tabla de hechos, sin año en el nombre (se particiona por fecha) |
| \`resumen_mensual\` | \`kpi_ventas_mensuales\` | Prefijo kpi_ indica que es una agregación |
| \`temp_productos\` | \`stg_productos\` | Si es staging; o \`dim_productos\` si es definitiva |

**2. Documentación de \`gold.kpi_ventas_diarias\`:**
| Campo | Valor |
|-------|-------|
| Nombre | gold.kpi_ventas_diarias |
| Propósito | Métricas de ventas agregadas por día para el dashboard comercial |
| Grano | Un registro por día |
| Fuente | gold.fact_ventas + gold.dim_tiempo |
| Frecuencia | Diaria a las 07:00 UTC |
| Columnas | fecha, total_ventas, cantidad_pedidos, ticket_promedio, clientes_unicos |
| Owner | Equipo Data Engineering |

**3. Investigar con linaje:**
Recorro el camino inverso: Gold \`kpi_ventas_mensuales\` ← ¿qué query lo genera? ← Gold \`fact_ventas\` ← ¿los datos están correctos ahí? ← Silver \`orders\` ← ¿la limpieza fue correcta? ← Bronze \`raw_orders\` ← ¿la fuente envió datos correctos? En cada paso verifico que los datos y las transformaciones sean correctas.

**4. Importancia del grano:**
Porque define **qué puedo y qué no puedo calcular** con esa tabla. Si el grano es "una venta por día" (agregado), no puedo saber las ventas individuales. Si el grano es "una línea de ticket" (detalle), puedo agregar hacia arriba (por día, por mes, por año). Sin saber el grano, un analista podría hacer cálculos incorrectos (ej: contar filas pensando que cada fila es una transacción cuando en realidad cada fila es un producto dentro de una transacción).`,
  },

  /* ╔══════════════════════════════════════════════════════════╗
     ║  SEMANA 3 — Modelado Dimensional                        ║
     ╚══════════════════════════════════════════════════════════╝ */

  // ── 12 ──
  {
    id: 12,
    level: 'SEMANA 3',
    title: 'Lección 12: Tablas de hechos (Facts)',
    description: 'Qué son, cómo se diseñan, tipos de facts, y cómo definir el grano correcto.',
    theory: `# Tablas de hechos (Facts)

## ¿Qué es una tabla de hechos?

Contiene las **métricas o eventos medibles** del negocio. Cada fila representa un hecho que ocurrió: una venta, un click, un envío, un pago.

## Anatomía de una tabla de hechos

| Tipo de columna | Propósito | Ejemplos |
|----------------|----------|---------|
| **Clave subrogada** | Identificador único | \`sk_venta\` (auto-incremental) |
| **Foreign keys** | Vinculan a dimensiones | \`sk_cliente\`, \`sk_producto\`, \`id_fecha\` |
| **Métricas** | Valores medibles | \`monto\`, \`cantidad\`, \`descuento\` |
| **Dim degenerada** | Atributo sin tabla propia | \`nro_factura\`, \`nro_ticket\` |

## El grano (Grain)

### ¿Qué es el grano?
Define **qué representa cada fila** de la tabla de hechos.

### Preguntas para definirlo:
- ¿Una fila = una transacción completa? ¿O una línea de esa transacción?
- ¿Una fila = un día? ¿O un registro individual?

### Regla de oro: siempre elegir el grano más fino posible

| Grano | Qué permite | Qué NO permite |
|-------|------------|----------------|
| **Línea de ticket** (fino) | Análisis por producto, por transacción, por día, por mes | Nada, permite todo |
| **Transacción completa** (medio) | Análisis por transacción, por día, por mes | No permite detallar por producto |
| **Ventas por día** (grueso) | Análisis por día, por mes | No permite ver transacciones individuales |

### Ejemplo:
Una compra de 3 productos:
- Con grano **línea de ticket**: 3 filas (una por producto)
- Con grano **transacción**: 1 fila (toda la compra)
- Con grano **diario**: se agrupa con todas las compras del día

## Tipos de tablas de hechos

### 1. Fact de transacciones
- Cada fila = un **evento que ocurrió**
- El más común
- Ejemplo: \`fact_ventas\`, \`fact_clicks\`, \`fact_pagos\`

### 2. Fact de snapshots periódicos
- Cada fila = el **estado en un momento**
- Se genera periódicamente (diario, mensual)
- Ejemplo: \`fact_saldo_diario\` (saldo de cada cuenta al cierre del día)

### 3. Fact de snapshots acumulativos
- Cada fila = un **proceso con hitos**
- Se actualiza a medida que avanza el proceso
- Ejemplo: \`fact_pedido_tracking\` (fecha_pedido, fecha_envío, fecha_entrega)

| Tipo | Cuándo se crea | Se actualiza? | Ejemplo |
|------|---------------|--------------|---------|
| **Transacción** | Cuando ocurre el evento | No | Venta registrada |
| **Snapshot periódico** | En intervalos fijos | No (se agrega nueva fila) | Saldo diario |
| **Snapshot acumulativo** | Al inicio del proceso | Sí (se actualizan hitos) | Tracking de pedido |`,
    exercise: {
      heading: 'Ejercicio: Tablas de hechos',
      instructions: [
        '1. Diseñá una `fact_ventas` para un supermercado. Definí: grano, columnas (FK + métricas), y a qué dimensiones se conecta.',
        '2. ¿Qué tipo de fact usarías para: (a) registrar cada llamada a un call center; (b) saldo bancario diario; (c) tracking de un envío (pedido → despacho → entrega)?',
        '3. Un analista quiere saber "¿cuál fue el producto más vendido del mes?". ¿Qué grano necesita tu fact? ¿Un grano de "ventas por día" le serviría?',
        '4. ¿Qué es una "dimensión degenerada"? Dá un ejemplo.',
      ],
      hint: 'El grano define qué preguntas podés responder. Grano fino = más flexibilidad.',
    },
    modelAnswer: `## Respuesta modelo

**1. \`fact_ventas\` para supermercado:**

**Grano:** una línea de ticket (un producto vendido en una transacción)

| Columna | Tipo | Descripción |
|---------|------|-------------|
| sk_venta | INT PK | Clave subrogada |
| sk_cliente | INT FK | → dim_clientes |
| sk_producto | INT FK | → dim_productos |
| sk_sucursal | INT FK | → dim_sucursales |
| id_fecha | INT FK | → dim_tiempo |
| nro_ticket | VARCHAR | Dimensión degenerada |
| cantidad | INT | Unidades vendidas |
| precio_unitario | DECIMAL | Precio por unidad |
| descuento | DECIMAL | Descuento aplicado |
| monto_total | DECIMAL | cantidad × precio - descuento |

**2. Tipos de fact:**
| Caso | Tipo | Justificación |
|------|------|---------------|
| Llamadas a call center | **Transacción** | Cada llamada es un evento puntual |
| Saldo bancario diario | **Snapshot periódico** | Captura el estado al cierre de cada día |
| Tracking de envío | **Snapshot acumulativo** | Es un proceso con hitos (pedido→despacho→entrega) |

**3. Producto más vendido:**
Necesita grano de **línea de ticket** (producto individual). Un grano de "ventas por día" no serviría porque agrupa todos los productos del día → no se puede distinguir cuál fue más vendido.

**4. Dimensión degenerada:**
Es un atributo que vive **directamente en la tabla de hechos** porque no justifica una tabla de dimensión propia. Ejemplo: \`nro_ticket\` o \`nro_factura\`. Son identificadores útiles para filtrar pero no tienen atributos adicionales que modelar.`,
  },

  // ── 13 ──
  {
    id: 13,
    level: 'SEMANA 3',
    title: 'Lección 13: Tablas de dimensiones',
    description: 'Qué son, cómo se diseñan, claves subrogadas, la dimensión de tiempo y dimensiones efectivas.',
    theory: `# Tablas de dimensiones

## ¿Qué es una dimensión?

Contiene los **atributos descriptivos** que dan contexto a los hechos. Responde al **quién, qué, dónde, cuándo** de cada evento.

## Características

| Aspecto | Detalle |
|---------|---------|
| **Filas** | Menos que la tabla de hechos |
| **Columnas** | Más que la tabla de hechos (muchos atributos) |
| **Contenido** | Atributos descriptivos, NO métricas |
| **Uso** | Filtrar, agrupar, segmentar |

## Claves subrogadas vs naturales

### Clave natural
- Identificador que **ya existe** en la fuente: DNI, código de producto, email
- **Problema**: puede cambiar, no ser único entre sistemas

### Clave subrogada (Surrogate Key)
- Identificador **artificial** generado por el Data Engineer
- Generalmente un número auto-incremental
- **Ventaja**: nunca cambia, es única, desacopla del sistema origen

| Aspecto | Clave natural | Clave subrogada |
|---------|-------------|----------------|
| **Origen** | Sistema fuente | Generada por el DE |
| **Puede cambiar** | Sí | Nunca |
| **Unicidad** | Puede no ser única entre sistemas | Siempre única |
| **Ejemplo** | \`CLI-4521\`, DNI \`32.456.789\` | \`1\`, \`2\`, \`3\`... |
| **Buena práctica** | Mantener como atributo | Usar como PK |

## La dimensión de tiempo (dim_tiempo)

La dimensión **más importante** y presente en casi todo modelo.

### Columnas típicas:

| Columna | Tipo | Ejemplo |
|---------|------|---------|
| \`id_fecha\` | INT | 20250215 (PK, formato YYYYMMDD) |
| \`fecha\` | DATE | 2025-02-15 |
| \`dia\` | INT | 15 |
| \`mes\` | INT | 2 |
| \`año\` | INT | 2025 |
| \`trimestre\` | INT | 1 |
| \`dia_semana\` | VARCHAR | Sábado |
| \`nombre_mes\` | VARCHAR | Febrero |
| \`es_fin_de_semana\` | BOOLEAN | true |
| \`es_feriado\` | BOOLEAN | false |
| \`semana_del_año\` | INT | 7 |

### ¿Por qué no usar directamente la fecha?
Porque la tabla de tiempo permite agregar atributos como feriados, períodos fiscales, o estacionalidad sin modificar las tablas de hechos.

## Reglas para dimensiones efectivas

1. **Una dimensión por concepto**: no mezclar clientes con productos
2. **Atributos descriptivos, no métricas**: las dimensiones describen, los hechos miden
3. **Desnormalizar cuando sea posible**: mejor tener \`ciudad\`, \`provincia\`, \`pais\` en una dimensión que en 3 tablas separadas
4. **Registro "Desconocido"**: siempre tener un registro con sk = -1 para hechos sin dimensión asociada

## Ejemplo: \`dim_clientes\`

| Columna | Tipo | Descripción |
|---------|------|-------------|
| \`sk_cliente\` | INT | PK subrogada |
| \`id_cliente_origen\` | VARCHAR | Clave natural del CRM |
| \`nombre\` | VARCHAR | Nombre completo |
| \`email\` | VARCHAR | Email de contacto |
| \`ciudad\` | VARCHAR | Ciudad de residencia |
| \`provincia\` | VARCHAR | Provincia |
| \`pais\` | VARCHAR | País |
| \`segmento\` | VARCHAR | Premium / Standard / Basic |
| \`fecha_alta\` | DATE | Cuándo se registró |`,
    exercise: {
      heading: 'Ejercicio: Dimensiones',
      instructions: [
        '1. Diseñá una `dim_productos` para un supermercado con al menos 8 columnas. Usá clave subrogada.',
        '2. ¿Por qué es mejor usar una clave subrogada que el código de producto del proveedor como PK?',
        '3. Diseñá una `dim_tiempo` con al menos 10 columnas. ¿Qué columna usarías como PK?',
        '4. Un hecho de venta no tiene cliente asociado (venta anónima). ¿Cómo lo manejás en el modelo dimensional?',
      ],
      hint: 'Las dimensiones son "tablas de contexto": describen las circunstancias de cada hecho.',
    },
    modelAnswer: `## Respuesta modelo

**1. \`dim_productos\`:**
| Columna | Tipo | Descripción |
|---------|------|-------------|
| \`sk_producto\` | INT | PK subrogada |
| \`codigo_producto\` | VARCHAR | Código del proveedor (clave natural) |
| \`nombre\` | VARCHAR | Nombre del producto |
| \`marca\` | VARCHAR | Marca |
| \`categoria\` | VARCHAR | Categoría (Lácteos, Bebidas, etc.) |
| \`subcategoria\` | VARCHAR | Subcategoría (Leche, Yogurt, etc.) |
| \`precio_lista\` | DECIMAL | Precio de lista actual |
| \`unidad_medida\` | VARCHAR | kg, litro, unidad |
| \`es_perecedero\` | BOOLEAN | ¿Es perecedero? |
| \`proveedor\` | VARCHAR | Nombre del proveedor |

**2. ¿Por qué clave subrogada?**
Porque el código del proveedor puede: (a) cambiar si el proveedor modifica su sistema; (b) colisionar si dos proveedores usan el mismo código para productos diferentes; (c) tener formato inconsistente entre sistemas. La clave subrogada es generada internamente, nunca cambia y es siempre única.

**3. \`dim_tiempo\`:**
PK: \`id_fecha\` en formato YYYYMMDD (ej: 20250215)
Columnas: id_fecha, fecha, dia, mes, año, trimestre, dia_semana, nombre_dia, nombre_mes, es_fin_de_semana, es_feriado, semana_del_año

**4. Venta anónima:**
Se vincula al registro **"Desconocido"** en dim_clientes (sk_cliente = -1, nombre = "Anónimo"). Así el hecho no queda huérfano y el modelo mantiene su integridad referencial. Se pueden agregar las ventas anónimas en cualquier análisis filtrando por sk_cliente = -1.`,
  },

  // ── 14 ──
  {
    id: 14,
    level: 'SEMANA 3',
    title: 'Lección 14: Star Schema y Snowflake Schema',
    description: 'Los dos modelos dimensionales principales, cuándo usar cada uno, y cómo se diagraman.',
    theory: `# Star Schema y Snowflake Schema

## Star Schema (Esquema Estrella)

La tabla de hechos está en el **centro** y las dimensiones se conectan **directamente** a ella.

### Diagrama:
\`\`\`
        dim_clientes
             │
dim_productos ── fact_ventas ── dim_tiempo
             │
        dim_tiendas
\`\`\`

### Características:

| Aspecto | Detalle |
|---------|---------|
| **Estructura** | Dimensiones conectadas directo a hechos |
| **JOINs necesarios** | Pocos (1 JOIN por dimensión) |
| **Redundancia** | Puede haber datos repetidos en dimensiones |
| **Velocidad** | Muy rápida para consultas |
| **Complejidad** | Baja |

### Ejemplo:
\`dim_productos\` tiene las columnas \`categoria\`, \`subcategoria\`, \`marca\` todas en la misma tabla.
Aunque "Lácteos" se repite en 50 productos, la consulta es un solo JOIN.

## Snowflake Schema (Esquema Copo de Nieve)

Las dimensiones se **normalizan** en sub-tablas.

### Diagrama:
\`\`\`
dim_subcategoria ── dim_categoria
                        │
         dim_marca ── dim_productos ── fact_ventas ── dim_tiempo
                                           │
                                      dim_clientes ── dim_ciudades ── dim_paises
\`\`\`

### Características:

| Aspecto | Detalle |
|---------|---------|
| **Estructura** | Dimensiones normalizadas en sub-tablas |
| **JOINs necesarios** | Muchos (hay cadenas de JOINs) |
| **Redundancia** | Mínima |
| **Velocidad** | Más lenta (más JOINs) |
| **Complejidad** | Alta |

## Comparación directa

| Aspecto | Star Schema | Snowflake Schema |
|---------|------------|-----------------|
| **Redundancia** | Sí (datos repetidos en dimensiones) | No (normalizado) |
| **JOINs** | Pocos y simples | Muchos y encadenados |
| **Velocidad de consulta** | Muy rápida | Más lenta |
| **Facilidad para analistas** | Alta (intuitivo) | Baja (complejo) |
| **Espacio en disco** | Más | Menos |
| **Mantenimiento** | Simple | Complejo |
| **Caso ideal** | Dashboards, BI, consultas frecuentes | Datos con muchas jerarquías, ahorro de espacio |

## ¿Cuándo usar cada uno?

### Star Schema: la opción por defecto
- Para la **mayoría de los casos**
- Cuando la velocidad de consulta es prioridad
- Cuando los analistas consultan directamente las tablas
- Cuando la redundancia no es un problema (dimensiones chicas)

### Snowflake Schema: casos específicos
- Cuando las dimensiones tienen **jerarquías muy profundas** (país → provincia → ciudad → barrio)
- Cuando el **espacio en disco** es una restricción real
- Cuando hay una capa intermedia que abstrae la complejidad (ej: un BI tool que hace los JOINs automáticos)

## Recomendación profesional

**Empezá siempre con Star Schema.** Solo migrá a Snowflake si tenés una razón concreta. En la práctica, el ahorro de espacio rara vez justifica la complejidad adicional.`,
    exercise: {
      heading: 'Ejercicio: Star vs Snowflake',
      instructions: [
        '1. Dibujá (con texto) un Star Schema para un **sistema de reservas de hotel** con al menos 4 dimensiones.',
        '2. ¿Cómo se vería el mismo modelo como Snowflake Schema? ¿Qué dimensiones normalizarías?',
        '3. Un analista se queja de que sus consultas son lentas. Está usando un Snowflake Schema con 8 tablas de dimensiones encadenadas. ¿Qué le recomendarías?',
        '4. ¿En qué caso concreto elegirías Snowflake sobre Star? Dá un ejemplo real.',
      ],
      hint: 'Star Schema es casi siempre la mejor opción. Snowflake solo se justifica en casos específicos.',
    },
    modelAnswer: `## Respuesta modelo

**1. Star Schema para hotel:**
\`\`\`
      dim_huespedes
           │
dim_habitaciones ── fact_reservas ── dim_tiempo
           │
      dim_canales (web, agencia, teléfono)
\`\`\`

**2. Snowflake del mismo modelo:**
\`\`\`
dim_tipo_habitacion ── dim_habitaciones ── fact_reservas ── dim_tiempo
      dim_piso ──┘                             │
                                          dim_huespedes ── dim_ciudades ── dim_paises
                                               │
                                          dim_canales ── dim_tipo_canal
\`\`\`
Se normalizaron: habitaciones (tipo + piso), huéspedes (ciudad + país), canales (tipo).

**3. Consultas lentas con Snowflake:**
Recomendaría **migrar a Star Schema**: desnormalizar las cadenas de JOINs metiendo los atributos directamente en la dimensión principal. En vez de dim_productos → dim_categorias → dim_departamentos, tener una sola dim_productos con columnas categoria y departamento. Menos JOINs = consultas mucho más rápidas.

**4. Caso para Snowflake:**
Un sistema geográfico con **jerarquía profunda**: país → región → provincia → departamento → ciudad → barrio → código postal, donde cada nivel tiene muchos atributos propios (población, presupuesto, gobernador, etc). En este caso, desnormalizar todo en una sola dimensión crearía una tabla con 40+ columnas muy redundantes. Snowflake permite mantener cada nivel como tabla separada con sus atributos específicos.`,
  },

  /* ╔══════════════════════════════════════════════════════════╗
     ║  SEMANA 4 — MERGE, Idempotencia y SCDs                  ║
     ╚══════════════════════════════════════════════════════════╝ */

  // ── 15 ──
  {
    id: 15,
    level: 'SEMANA 4',
    title: 'Lección 15: MERGE y Upsert',
    description: 'Qué es MERGE, qué es un Upsert, y cuándo usar cada patrón de escritura.',
    theory: `# MERGE y Upsert

## ¿Qué es un MERGE?

MERGE es una operación que combina **inserción, actualización y eliminación** en una sola instrucción. Compara una fuente contra un destino y decide qué hacer con cada registro.

## Lógica conceptual

\`\`\`
Para cada registro de la FUENTE:
  SI existe en el DESTINO (match por clave):
    → ACTUALIZAR los campos que cambiaron
  SI NO existe en el DESTINO:
    → INSERTAR como registro nuevo
  (opcional) SI existe en DESTINO pero NO en FUENTE:
    → ELIMINAR del destino
\`\`\`

## ¿Qué es un Upsert?

Es un caso simplificado de MERGE: **insertar si no existe, actualizar si existe**. Sin eliminación.

| Operación | INSERT | UPDATE | DELETE |
|-----------|--------|--------|--------|
| **INSERT** | ✅ | ❌ | ❌ |
| **UPDATE** | ❌ | ✅ | ❌ |
| **Upsert** | ✅ | ✅ | ❌ |
| **MERGE completo** | ✅ | ✅ | ✅ |

## ¿Cuándo usar cada uno?

| Patrón | Caso de uso | Ejemplo |
|--------|------------|---------|
| **INSERT** | Datos que solo se crean, nunca se modifican | Logs, eventos, transacciones |
| **UPDATE** | Actualizar registros existentes sin crear nuevos | Corregir un error específico |
| **Upsert** | Sincronizar datos sin eliminar | Actualizar catálogo de productos |
| **MERGE completo** | Espejo exacto de la fuente | Sincronizar tabla de precios vigentes |

## Ejemplo: actualizar dim_clientes

### Datos actuales en dim_clientes:
| sk | id_origen | nombre | email | ciudad |
|----|----------|--------|-------|--------|
| 1 | CLI-101 | Ana García | ana@mail.com | Buenos Aires |
| 2 | CLI-102 | Carlos López | carlos@mail.com | Córdoba |

### Datos nuevos del CRM:
| id_origen | nombre | email | ciudad |
|----------|--------|-------|--------|
| CLI-101 | Ana García | ana@nuevo.com | Buenos Aires |
| CLI-103 | María Ruiz | maria@mail.com | Rosario |

### Resultado después del MERGE:
| sk | id_origen | nombre | email | ciudad | Acción |
|----|----------|--------|-------|--------|--------|
| 1 | CLI-101 | Ana García | **ana@nuevo.com** | Buenos Aires | **ACTUALIZADO** (email cambió) |
| 2 | CLI-102 | Carlos López | carlos@mail.com | Córdoba | Sin cambios |
| 3 | CLI-103 | María Ruiz | maria@mail.com | Rosario | **INSERTADO** (nuevo) |

## Consideraciones importantes

- **Clave de match**: necesitás definir POR QUÉ campo se comparan los registros (ej: \`id_origen\`)
- **Qué actualizar**: no siempre querés actualizar TODOS los campos, solo los que cambiaron
- **Orden importa**: si la fuente tiene duplicados, el MERGE puede fallar. Siempre deduplicar la fuente primero`,
    exercise: {
      heading: 'Ejercicio: MERGE y Upsert',
      instructions: [
        '1. Explicá la diferencia entre INSERT, UPDATE, Upsert y MERGE completo.',
        '2. Tenés una tabla de precios de productos. El proveedor envía la lista actualizada cada día. Algunos productos cambiaron de precio, algunos son nuevos, algunos ya no están. ¿Qué operación usarías? ¿Por qué?',
        '3. Tenés una tabla de logs de errores. ¿Usarías MERGE para cargar datos nuevos? ¿O INSERT? Justificá.',
        '4. El MERGE falla con el error "hay múltiples registros en la fuente con la misma clave". ¿Qué significa y cómo lo resolvés?',
      ],
      hint: 'Upsert es más seguro que MERGE completo porque no elimina datos. Usá MERGE completo solo cuando necesitás un espejo exacto.',
    },
    modelAnswer: `## Respuesta modelo

**1. Diferencias:**
| Operación | Hace |
|-----------|------|
| **INSERT** | Solo crea registros nuevos. Si ya existe, falla o duplica. |
| **UPDATE** | Solo modifica registros existentes. No crea nuevos. |
| **Upsert** | Si existe → actualiza. Si no existe → inserta. No elimina nada. |
| **MERGE completo** | Inserta nuevos + actualiza existentes + elimina los que ya no están en la fuente. |

**2. Precios de productos → MERGE completo:**
Porque necesitamos que la tabla refleje exactamente lo que el proveedor envía: productos nuevos se insertan, precios actualizados se modifican, y productos descontinuados (que ya no están en la lista) se eliminan o marcan como inactivos.

**3. Logs de errores → INSERT:**
Los logs son **append-only**: cada registro es un evento único que ocurrió y nunca se modifica. Usar MERGE no tiene sentido porque no hay registros que actualizar ni eliminar. INSERT es la operación correcta.

**4. Múltiples registros con misma clave:**
Significa que la **fuente tiene duplicados**: dos o más filas con el mismo valor en la clave de match. El MERGE no sabe cuál usar para la actualización. Solución: **deduplicar la fuente antes del MERGE**, eligiendo el registro más reciente o más completo para cada clave.`,
  },

  // ── 16 ──
  {
    id: 16,
    level: 'SEMANA 4',
    title: 'Lección 16: Idempotencia',
    description: 'El concepto más importante de Data Engineering: qué es, por qué es crítica, y patrones para lograrla.',
    theory: `# Idempotencia

## Definición

Una operación es **idempotente** si ejecutarla **una vez o múltiples veces** produce el **mismo resultado**.

## ¿Por qué es el concepto más importante?

Porque los pipelines **fallan**. Siempre. Es inevitable.

| Causa de falla | Ejemplo |
|---------------|---------|
| Error de red | La API no responde |
| Timeout | La consulta tardó más de lo esperado |
| Datos corruptos | La fuente envió registros inválidos |
| Recurso agotado | Se acabó la memoria o disco |
| Bug en el código | Una transformación tiene un error |
| Cambio en la fuente | La fuente agregó una columna nueva |

**Si un pipeline falla, necesitamos poder re-ejecutarlo con confianza de que no generará duplicados, no perderá datos, y el resultado será correcto.**

## Ejemplo: pipeline NO idempotente

\`\`\`
Pipeline: "Insertar ventas del día"

1ra ejecución: INSERTA 1000 registros → OK
Falla a mitad → re-ejecutar
2da ejecución: INSERTA 1000 registros OTRA VEZ → 2000 registros (¡DUPLICADOS!)
\`\`\`

## Ejemplo: pipeline idempotente

\`\`\`
Pipeline: "Reemplazar ventas del día"

1ra ejecución: BORRA ventas del día + INSERTA 1000 registros → OK
Falla a mitad → re-ejecutar
2da ejecución: BORRA ventas del día + INSERTA 1000 registros → 1000 registros (CORRECTO)
\`\`\`

## Patrones de idempotencia

### 1. INSERT OVERWRITE (Reemplazar partición)

\`\`\`
1. Identificar la partición (ej: fecha = 2025-02-15)
2. BORRAR todos los registros de esa partición
3. INSERTAR todos los registros de esa partición
\`\`\`

| Ventaja | Desventaja |
|---------|-----------|
| Simple de implementar | Borra y reinserta todo (puede ser lento) |
| Siempre deja datos correctos | Necesita una columna de partición clara |

### 2. MERGE / Upsert

\`\`\`
1. Comparar fuente con destino por clave
2. Si existe → actualizar
3. Si no existe → insertar
\`\`\`

| Ventaja | Desventaja |
|---------|-----------|
| No borra datos | Más complejo de implementar |
| Eficiente (solo cambia lo necesario) | Necesita una clave de match confiable |

### 3. Tabla temporal + swap

\`\`\`
1. Crear tabla TEMPORAL con datos nuevos
2. Validar que la temporal está correcta
3. REEMPLAZAR la tabla principal por la temporal
\`\`\`

| Ventaja | Desventaja |
|---------|-----------|
| Operación atómica (todo o nada) | Usa más espacio temporalmente |
| Se puede validar antes de aplicar | Más pasos |

## Test de idempotencia

Para verificar si tu pipeline es idempotente:
1. Ejecutalo una vez → anotá el resultado
2. Ejecutalo de nuevo **sin cambiar los datos de origen**
3. ¿El resultado es **exactamente igual**?
   - Sí → es idempotente ✅
   - No (tiene duplicados o datos faltantes) → NO es idempotente ❌`,
    exercise: {
      heading: 'Ejercicio: Idempotencia',
      instructions: [
        '1. Explicá con tus palabras qué es idempotencia y por qué es crítica.',
        '2. Este pipeline es idempotente? "INSERT INTO ventas SELECT * FROM staging_ventas WHERE fecha = hoy". Justificá.',
        '3. ¿Cómo harías idempotente el pipeline del punto 2?',
        '4. Un pipeline falla después de insertar 500 de 1000 registros. Describí qué pasa si: (a) NO es idempotente; (b) SÍ es idempotente.',
        '5. ¿Cuál de los 3 patrones de idempotencia usarías para cargar dim_clientes? ¿Y para cargar fact_ventas diarias?',
      ],
      hint: 'Preguntate: "si ejecuto esto 3 veces seguidas, ¿el resultado es el mismo que ejecutarlo 1 vez?"',
    },
    modelAnswer: `## Respuesta modelo

**1. Idempotencia:**
Es la propiedad de que una operación produzca el mismo resultado sin importar cuántas veces se ejecute. Es crítica porque los pipelines fallan inevitablemente (errores de red, bugs, timeouts) y necesitamos poder re-ejecutarlos sin miedo a duplicar datos o perder registros.

**2. ¿Es idempotente?**
**NO.** Si se ejecuta 2 veces, inserta los registros del día 2 veces → duplicados. INSERT no verifica si los datos ya existen.

**3. Hacerlo idempotente:**
Opción A (INSERT OVERWRITE): "DELETE FROM ventas WHERE fecha = hoy; INSERT INTO ventas SELECT * FROM staging_ventas WHERE fecha = hoy"
Opción B (MERGE): comparar por clave primaria, insertar si no existe, actualizar si existe.

**4. Falla a mitad de camino:**
| Escenario | Resultado |
|-----------|----------|
| **No idempotente** | Quedan 500 registros. Al re-ejecutar, se insertan 1000 más → 1500 total (500 duplicados + 500 que faltaban + 500 nuevos) |
| **Idempotente (INSERT OVERWRITE)** | Al re-ejecutar: borra todo del día + inserta los 1000 completos → 1000 exactos |

**5. Patrones recomendados:**
- **dim_clientes** → **MERGE/Upsert**: porque los clientes se actualizan (cambian de email, ciudad) pero no se eliminan. MERGE actualiza existentes e inserta nuevos.
- **fact_ventas diarias** → **INSERT OVERWRITE**: porque las ventas de un día son un conjunto cerrado. Es más simple borrar el día y reinsertar todo que hacer MERGE registro por registro.`,
  },

  // ── 17 ──
  {
    id: 17,
    level: 'SEMANA 4',
    title: 'Lección 17: SCD Type 1 — Sobrescribir sin historia',
    description: 'Qué es SCD Type 1, cuándo usarlo, y cómo implementarlo conceptualmente.',
    theory: `# SCD Type 1 — Sobrescribir sin historia

## ¿Qué son las SCDs?

Las dimensiones **cambian con el tiempo**. Un cliente se muda, un producto cambia de categoría, un empleado cambia de departamento.

**SCD = Slowly Changing Dimensions** (Dimensiones que cambian lentamente).

La pregunta clave es: **¿queremos conservar el historial de esos cambios?**

## SCD Type 1: Sobrescribir

### Comportamiento:
Se **reemplaza** el valor anterior con el nuevo. No se guarda ningún historial.

### Ejemplo: cliente se muda de ciudad

**Antes:**
| sk_cliente | nombre | ciudad | segmento |
|-----------|--------|--------|----------|
| 1 | Ana García | Buenos Aires | Premium |

**Después (se aplica Type 1):**
| sk_cliente | nombre | ciudad | segmento |
|-----------|--------|--------|----------|
| 1 | Ana García | **Córdoba** | Premium |

Buenos Aires **desaparece**. No hay forma de saber dónde vivía antes.

### ¿Cómo se implementa?
\`\`\`
1. Llegan datos nuevos del CRM
2. Se busca el cliente por clave natural (id_cliente_origen)
3. SI existe → se ACTUALIZAN los campos que cambiaron
4. SI no existe → se INSERTA como nuevo registro
\`\`\`

Es básicamente un **Upsert**.

## ¿Cuándo usar Type 1?

| Caso de uso | ¿Type 1 es apropiado? |
|------------|----------------------|
| Corrección de errores (nombre mal escrito) | ✅ Sí — no hay historia relevante |
| Actualización de email o teléfono | ✅ Sí — generalmente solo importa el actual |
| Cambio de categoría de producto | ⚠️ Depende — ¿necesitás el historial de categorías? |
| Cambio de precio de producto | ❌ No — perdés la historia de precios |
| Cambio de dirección de cliente | ⚠️ Depende — ¿necesitás saber dónde vivía antes? |

## Ventajas y desventajas

| Ventaja | Desventaja |
|---------|-----------|
| **Simple** de implementar | **Pierde historia** del cambio |
| **Rápido**: solo un UPDATE | No permite análisis temporal |
| **Espacio eficiente**: sin filas extra | No hay auditoría del cambio |
| La dimensión no crece con cambios | No podés responder "¿dónde vivía el cliente cuando compró X?" |

## Lo que NO podés responder con Type 1

Si un analista pregunta: "¿Cuántas ventas hizo Ana cuando vivía en Buenos Aires?", con Type 1 **no se puede responder** porque ya no existe el registro de que vivía en Buenos Aires.

Para eso necesitás **SCD Type 2** (próxima lección).`,
    exercise: {
      heading: 'Ejercicio: SCD Type 1',
      instructions: [
        '1. Explicá SCD Type 1 con tus propias palabras.',
        '2. Un producto cambia de nombre: de "Coca-Cola 500ml" a "Coca-Cola 500cc". ¿Usarías Type 1? ¿Por qué?',
        '3. Un empleado cambia de departamento (de "Ventas" a "Marketing"). ¿Type 1 es apropiado? ¿Qué información perdés?',
        '4. Mostrá el estado de la tabla antes y después de aplicar Type 1 al siguiente cambio: el cliente Carlos López (sk=2) cambió su email de carlos@old.com a carlos@new.com.',
      ],
      hint: 'Type 1 es perfecto para correcciones o cuando solo importa el valor actual.',
    },
    modelAnswer: `## Respuesta modelo

**1. SCD Type 1:**
Es la estrategia más simple para manejar cambios en dimensiones: cuando un valor cambia, simplemente se sobrescribe el anterior. No se guarda historia. Es un UPDATE directo sobre el registro existente.

**2. Cambio de nombre de producto:**
**Sí, Type 1 es correcto.** Es una corrección de formato/nomenclatura, no un cambio de negocio. No tiene sentido guardar el nombre anterior "500ml" vs "500cc"; siempre queremos mostrar el nombre actual. No hay valor analítico en el nombre viejo.

**3. Empleado cambia de departamento:**
**Depende.** Si solo necesito saber en qué departamento está AHORA, Type 1 está bien. Pero si necesito responder "¿cuántas ventas cerró cuando estaba en Ventas?", pierdo esa información. En general, para departamento de empleado conviene **Type 2** porque el historial tiene valor analítico.

**4. Tabla antes y después:**

**Antes:**
| sk_cliente | nombre | email | ciudad |
|-----------|--------|-------|--------|
| 2 | Carlos López | carlos@old.com | Córdoba |

**Después (Type 1 aplicado):**
| sk_cliente | nombre | email | ciudad |
|-----------|--------|-------|--------|
| 2 | Carlos López | **carlos@new.com** | Córdoba |

Solo cambió el email. El sk_cliente sigue siendo 2 (misma fila, mismo registro).`,
  },

  // ── 18 ──
  {
    id: 18,
    level: 'SEMANA 4',
    title: 'Lección 18: SCD Type 2 — Historial completo',
    description: 'Qué es SCD Type 2, cómo mantener el historial de cambios, y las columnas de control necesarias.',
    theory: `# SCD Type 2 — Historial completo

## Comportamiento

Se **cierra** el registro actual y se **crea uno nuevo** con los datos actualizados. Se mantiene todo el historial.

## Ejemplo: cliente se muda de Buenos Aires a Córdoba

### Antes (1 registro):
| sk | id_cliente | nombre | ciudad | fecha_desde | fecha_hasta | es_actual |
|----|-----------|--------|--------|------------|------------|----------|
| 1 | CLI-101 | Ana García | Buenos Aires | 2024-01-15 | 9999-12-31 | true |

### Después (2 registros):
| sk | id_cliente | nombre | ciudad | fecha_desde | fecha_hasta | es_actual |
|----|-----------|--------|--------|------------|------------|----------|
| 1 | CLI-101 | Ana García | Buenos Aires | 2024-01-15 | **2025-02-14** | **false** |
| **2** | CLI-101 | Ana García | **Córdoba** | **2025-02-15** | 9999-12-31 | **true** |

### ¿Qué pasó?
1. El registro antiguo se **cerró**: \`fecha_hasta\` pasó de 9999-12-31 a 2025-02-14, y \`es_actual\` pasó a false
2. Se **creó un nuevo registro** con sk=2, la nueva ciudad, y \`es_actual\` = true

## Columnas de control necesarias

| Columna | Tipo | Propósito |
|---------|------|----------|
| \`fecha_desde\` | DATE | Cuándo empezó a ser válido este registro |
| \`fecha_hasta\` | DATE | Cuándo dejó de ser válido (9999-12-31 = activo) |
| \`es_actual\` | BOOLEAN | Flag para facilitar consultas del estado actual |

### ¿Por qué las 3 columnas?
- \`fecha_desde\` + \`fecha_hasta\` → permiten consultas "as of" (¿cómo era en tal fecha?)
- \`es_actual\` → facilita obtener el estado actual sin filtrar por fechas

## Consultas típicas con Type 2

### "¿Dónde vive Ana AHORA?"
\`\`\`
Filtrar: id_cliente = CLI-101 AND es_actual = true
Resultado: Córdoba
\`\`\`

### "¿Dónde vivía Ana cuando compró en enero 2025?"
\`\`\`
Filtrar: id_cliente = CLI-101 
  AND fecha_desde <= 2025-01-15 
  AND fecha_hasta >= 2025-01-15
Resultado: Buenos Aires
\`\`\`

## Proceso de actualización

\`\`\`
1. Llegan datos nuevos del CRM
2. Para cada registro nuevo, buscar en la dimensión por clave natural
3. SI el registro NO cambió → no hacer nada
4. SI el registro CAMBIÓ:
   a. Cerrar el registro actual (fecha_hasta = ayer, es_actual = false)
   b. Insertar nuevo registro (fecha_desde = hoy, fecha_hasta = 9999-12-31, es_actual = true)
5. SI el registro es NUEVO:
   a. Insertar (fecha_desde = hoy, fecha_hasta = 9999-12-31, es_actual = true)
\`\`\`

## Ventajas y desventajas

| Ventaja | Desventaja |
|---------|-----------|
| **Historial completo** de todos los cambios | La tabla **crece** con cada cambio |
| Permite análisis temporal | Más **complejo** de implementar |
| Auditoría completa | Consultas más complejas (hay que filtrar por es_actual) |
| Responde "¿cómo era en tal fecha?" | Más espacio en disco |

## Comparación con Type 1

| Aspecto | Type 1 | Type 2 |
|---------|--------|--------|
| **Historia** | No conserva | Conserva todo |
| **Filas** | Misma cantidad | Crecen con cambios |
| **Complejidad** | Baja (solo UPDATE) | Alta (cerrar + insertar) |
| **Análisis temporal** | No posible | Posible |
| **Caso ideal** | Correcciones | Auditoría, análisis por período |`,
    exercise: {
      heading: 'Ejercicio: SCD Type 2',
      instructions: [
        '1. Un empleado cambia de departamento: de "Ventas" a "Marketing" el 1 de marzo de 2025. Mostrá la tabla ANTES y DESPUÉS aplicando Type 2.',
        '2. Mismo empleado ahora cambia a "Dirección" el 15 de julio de 2025. Mostrá cómo queda la tabla con los 3 registros.',
        '3. Un analista pregunta: "¿En qué departamento estaba este empleado el 1 de mayo de 2025?" ¿Cómo lo consultarías?',
        '4. ¿Cuándo usarías Type 1 vs Type 2 para la dimensión de productos? Dá un ejemplo de cada uno.',
      ],
      hint: 'Cada cambio en Type 2 genera una fila nueva. El historial se reconstruye con fecha_desde y fecha_hasta.',
    },
    modelAnswer: `## Respuesta modelo

**1. Empleado cambia a Marketing (1 marzo 2025):**

**ANTES:**
| sk | id_emp | nombre | depto | fecha_desde | fecha_hasta | es_actual |
|----|--------|--------|-------|------------|------------|----------|
| 1 | EMP-200 | Juan Pérez | Ventas | 2023-03-01 | 9999-12-31 | true |

**DESPUÉS:**
| sk | id_emp | nombre | depto | fecha_desde | fecha_hasta | es_actual |
|----|--------|--------|-------|------------|------------|----------|
| 1 | EMP-200 | Juan Pérez | Ventas | 2023-03-01 | **2025-02-28** | **false** |
| **2** | EMP-200 | Juan Pérez | **Marketing** | **2025-03-01** | 9999-12-31 | **true** |

**2. Cambia a Dirección (15 julio 2025):**
| sk | id_emp | nombre | depto | fecha_desde | fecha_hasta | es_actual |
|----|--------|--------|-------|------------|------------|----------|
| 1 | EMP-200 | Juan Pérez | Ventas | 2023-03-01 | 2025-02-28 | false |
| 2 | EMP-200 | Juan Pérez | Marketing | 2025-03-01 | **2025-07-14** | **false** |
| **3** | EMP-200 | Juan Pérez | **Dirección** | **2025-07-15** | 9999-12-31 | **true** |

**3. ¿Departamento el 1 de mayo de 2025?**
Buscar: id_emp = EMP-200 AND fecha_desde <= 2025-05-01 AND fecha_hasta >= 2025-05-01
Resultado: **Marketing** (sk=2, que va del 1 marzo al 14 julio).

**4. Type 1 vs Type 2 para productos:**
- **Type 1**: corrección del nombre del producto (de "Coca Cola" a "Coca-Cola") → es una corrección, no un cambio de negocio
- **Type 2**: cambio de precio del producto → necesitamos saber a qué precio se vendió cada unidad en cada momento para análisis de rentabilidad`,
  },

  /* ╔══════════════════════════════════════════════════════════╗
     ║  SEMANA 5 — ACID, Optimización, Orquestación            ║
     ╚══════════════════════════════════════════════════════════╝ */

  // ── 19 ──
  {
    id: 19,
    level: 'SEMANA 5',
    title: 'Lección 19: Propiedades ACID y Time Travel',
    description: 'Transacciones ACID, Time Travel, VACUUM y los formatos de tabla abiertos.',
    theory: `# Propiedades ACID y Time Travel

## Transacciones ACID

**ACID** garantiza que las operaciones sobre datos sean confiables.

| Propiedad | Significado | Ejemplo |
|-----------|-----------|---------|
| **A - Atomicidad** | Todo o nada: la operación se completa entera o se revierte | Si falla al registro 500K de 1M, los 499.999 anteriores se revierten |
| **C - Consistencia** | Los datos pasan de un estado válido a otro válido | Si una columna no acepta NULL, ninguna operación puede dejar un NULL |
| **I - Aislamiento** | Operaciones paralelas no se interfieren | Mientras un pipeline escribe, un dashboard lee la versión anterior |
| **D - Durabilidad** | Una vez confirmado (commit), los datos persisten | Si el servidor se apaga tras el commit, los datos sobreviven |

## Time Travel

### ¿Qué es?
La capacidad de **acceder a versiones anteriores** de una tabla.

### Casos de uso:

| Caso | Ejemplo |
|------|---------|
| **Debugging** | "¿Qué datos tenía esta tabla ayer?" |
| **Auditoría** | "¿Quién cambió qué y cuándo?" |
| **Rollback** | "El pipeline corrompió datos, quiero volver a la versión de ayer" |
| **Comparación** | "¿Qué cambió entre la versión de ayer y la de hoy?" |

## VACUUM: limpieza de versiones

| Aspecto | Detalle |
|---------|---------|
| **Qué hace** | Elimina versiones antiguas de los datos |
| **Por qué** | Time Travel acumula versiones → ocupa espacio |
| **Configuración** | Período de retención (ej: 7 días, 30 días) |
| **Riesgo** | Después de VACUUM no se puede hacer Time Travel más allá del período |

## Formatos de tabla abiertos

| Formato | Creado por | Fortaleza principal |
|---------|-----------|-------------------|
| **Delta Lake** | Databricks | ACID + schema evolution |
| **Apache Iceberg** | Netflix | Hidden partitioning + snapshots |
| **Apache Hudi** | Uber | Upserts eficientes |

### ¿Qué tienen en común?
- Transacciones ACID sobre archivos
- Time Travel y versionado
- Schema evolution
- Open source, sin vendor lock-in`,
    exercise: {
      heading: 'Ejercicio: ACID y Time Travel',
      instructions: [
        '1. Un pipeline carga 500K registros pero falla en el registro 300K. Explicá qué hace la Atomicidad.',
        '2. Dos pipelines escriben en la misma tabla al mismo tiempo. ¿Qué propiedad de ACID previene la corrupción?',
        '3. Dá un ejemplo concreto donde Time Travel te salvaría de un desastre.',
        '4. ¿Qué pasa si hacés VACUUM con retención de 7 días e intentás acceder a una versión de hace 10 días?',
      ],
      hint: 'ACID es un "contrato de seguridad" para tus datos.',
    },
    modelAnswer: `## Respuesta modelo

**1. Atomicidad ante falla:**
La transacción es "todo o nada". Al fallar en el registro 300K, los 299.999 anteriores se **revierten automáticamente** (rollback). La tabla queda exactamente como estaba antes. No existen datos "a medio cargar".

**2. Escrituras simultáneas → Aislamiento:**
La propiedad de **Aislamiento** garantiza que las transacciones concurrentes no se interfieran. Si pipeline A está escribiendo y pipeline B también, cada uno ve una versión consistente y los conflictos se resuelven (ej: optimistic concurrency).

**3. Time Travel como salvación:**
Un Data Engineer ejecuta un pipeline que por un bug borra toda la tabla de clientes. Sin Time Travel: restaurar de backup (horas/días). Con Time Travel: acceder a la versión anterior (ej: "tabla como estaba hace 1 hora") y restaurar en minutos.

**4. VACUUM y versiones antiguas:**
Recibirás un **error**: los archivos de esa versión ya fueron eliminados por VACUUM. Es irreversible. Por eso es importante definir un período de retención razonable y comunicar al equipo.`,
  },

  // ── 20 ──
  {
    id: 20,
    level: 'SEMANA 5',
    title: 'Lección 20: Estrategias de optimización',
    description: 'Particionamiento, Z-ORDER, Liquid Clustering y vistas materializadas.',
    theory: `# Estrategias de optimización

## ¿Por qué optimizar?

A medida que los datos crecen, las consultas se vuelven más lentas. El objetivo: que las consultas **lean la menor cantidad de datos posible**.

## Particionamiento

### ¿Qué es?
Dividir una tabla en **segmentos** basados en el valor de una columna.

### Ejemplo: particionado por fecha
\`\`\`
fact_ventas/
  año=2024/mes=01/  → archivos de enero 2024
  año=2024/mes=02/  → archivos de febrero 2024
  año=2025/mes=01/  → archivos de enero 2025
\`\`\`

### ¿Cuándo SÍ particionar?

| Condición | ¿Particionar? |
|-----------|--------------|
| Tabla con millones de registros | ✅ Sí |
| Consultas que SIEMPRE filtran por esa columna | ✅ Sí |
| Columna de baja cardinalidad (fecha, país, categoría) | ✅ Sí |
| Tabla chica (< 1M registros) | ❌ No |
| Columna de alta cardinalidad (id_usuario) | ❌ No |

### El peligro: "small file problem"
Particionar por una columna con millones de valores distintos crea millones de particiones minúsculas → peor rendimiento.

## Z-ORDER

### ¿Qué es?
Reorganiza los datos **dentro de los archivos** para que registros similares queden juntos.

| Aspecto | Particionamiento | Z-ORDER |
|---------|-----------------|---------|
| **Cómo funciona** | Divide en carpetas | Ordena dentro de archivos |
| **Cardinalidad ideal** | Baja (fecha, país) | Alta (id_cliente, id_producto) |
| **Se complementan** | Sí — uno no reemplaza al otro |

## Liquid Clustering

Evolución de Z-ORDER: clustering **adaptativo e incremental**.

| Aspecto | Z-ORDER | Liquid Clustering |
|---------|---------|-------------------|
| **Alcance** | Reordena toda la tabla | Incremental (solo datos nuevos) |
| **Costo** | Alto (cada ejecución) | Bajo (adaptativo) |
| **Flexibilidad** | Fijo | Se adapta a patrones de consulta |

## Vistas materializadas

| Aspecto | Vista normal | Vista materializada |
|---------|-------------|-------------------|
| **Almacenamiento** | No almacena datos | Almacena el resultado |
| **Velocidad** | Se calcula al consultar | Resultado pregenerado |
| **Actualización** | Siempre al día | Necesita refresh |
| **Caso ideal** | Consultas poco frecuentes | Dashboards con consultas repetitivas |`,
    exercise: {
      heading: 'Ejercicio: Optimización',
      instructions: [
        '1. Tabla de 500M de logs, siempre se filtra por fecha. ¿Cómo la optimizarías?',
        '2. Misma tabla, pero también filtran por id_usuario (2M usuarios). ¿Qué técnica adicional usarías?',
        '3. ¿Cuál es el riesgo de particionar por id_usuario?',
        '4. Un dashboard tarda 30 segundos en cargar. Muestra ventas por día y categoría. ¿Qué recomendarías?',
      ],
      hint: 'Particioná por baja cardinalidad, Z-ORDER por alta cardinalidad.',
    },
    modelAnswer: `## Respuesta modelo

**1. Logs con filtro por fecha → Particionamiento por fecha (año/mes/día):**
Con 500M de registros, cada día tiene un volumen manejable. Las consultas que filtran por fecha solo leen las particiones relevantes.

**2. Filtro adicional por id_usuario → Z-ORDER por id_usuario:**
No se puede particionar por id_usuario (2M particiones minúsculas). Z-ORDER organiza los datos dentro de las particiones de fecha para que los registros del mismo usuario queden juntos → data skipping.

**3. Riesgo de particionar por id_usuario:**
**Small file problem**: 2 millones de particiones con archivos diminutos. El sistema necesita abrir millones de archivos pequeños, lo cual es más lento que leer pocos archivos grandes.

**4. Dashboard lento → Vista materializada:**
Crear una vista materializada que precalcule ventas por día y categoría. El dashboard consulta el resultado pregenerado (instantáneo) en vez de calcular la agregación sobre millones de filas cada vez.`,
  },

  // ── 21 ──
  {
    id: 21,
    level: 'SEMANA 5',
    title: 'Lección 21: Orquestación de pipelines',
    description: 'DAGs, scheduling, manejo de errores y monitoreo de pipelines.',
    theory: `# Orquestación de pipelines

## ¿Qué es la orquestación?

La **coordinación automática** de múltiples tareas que forman un pipeline de datos.

## DAGs (Directed Acyclic Graphs)

Un **grafo dirigido acíclico** que representa tareas y dependencias.

### Ejemplo:
\`\`\`
ingestar_ventas ──┐
                  ├── limpiar_datos ── crear_gold ── notificar
ingestar_clientes ┘         │
                     crear_dim_clientes
\`\`\`

### Reglas del DAG:
| Regla | Significado |
|-------|-----------|
| **Dirigido** | Las flechas tienen dirección (A → B, no A ↔ B) |
| **Acíclico** | No hay ciclos (A → B → C → A no permitido) |
| **Dependencias** | Una tarea solo empieza cuando sus dependencias terminaron |

## Scheduling

| Tipo de trigger | Ejemplo |
|----------------|---------|
| **Temporal (Cron)** | "Todos los días a las 6:00 AM" |
| **Por evento** | "Cuando llega un archivo nuevo" |
| **Manual** | "El Data Engineer lo dispara" |
| **Por dependencia** | "Cuando pipeline_ventas termine" |

## Manejo de errores

| Estrategia | Detalle |
|-----------|---------|
| **Reintentos** | 3 reintentos con espera exponencial (1min, 5min, 15min) |
| **Alertas** | Notificar por Slack/email cuando falla |
| **Circuit breaker** | Si falla N veces seguidas, dejar de reintentar |
| **Idempotencia** | Las tareas DEBEN ser idempotentes para que los reintentos sean seguros |

## Monitoreo

### Métricas clave:

| Métrica | Alerta si... |
|---------|-------------|
| **Tiempo de ejecución** | Supera 2x el promedio |
| **Freshness** | Los datos no se actualizaron en X horas |
| **Volumen** | Se procesaron <50% de los registros habituales |
| **Tasa de éxito** | Cae por debajo del 95% semanal |

## Herramientas (conceptual)

| Herramienta | Tipo |
|------------|------|
| **Apache Airflow** | El más popular, open source |
| **Prefect** | Alternativa moderna a Airflow |
| **Dagster** | Enfocado en data assets |
| **Workflows nativos** | Cada plataforma cloud tiene el suyo |`,
    exercise: {
      heading: 'Ejercicio: Orquestación',
      instructions: [
        '1. Dibujá (con texto) el DAG de un pipeline para un e-commerce: ingesta ventas, ingesta productos, limpia ambos, crea Gold de ventas por categoría, envía reporte.',
        '2. ¿Qué scheduling le pondrías y por qué?',
        '3. "Limpiar ventas" falla 3 veces seguidas. ¿Qué configuración de error handling recomendás?',
        '4. ¿Qué métricas monitorearías para saber que el pipeline está sano?',
      ],
      hint: 'El DAG es el "plano de construcción" del pipeline.',
    },
    modelAnswer: `## Respuesta modelo

**1. DAG:**
\`\`\`
ingestar_ventas ──── limpiar_ventas ────┐
                                        ├── crear_gold_ventas_x_cat ── enviar_reporte
ingestar_productos ─ limpiar_productos ─┘
\`\`\`

**2. Scheduling:**
Cron diario a las 6:00 AM. Razón: datos se acumulan durante el día, a las 6 AM del día siguiente ya están completos. Reporte listo antes de las 9 AM cuando llega el equipo.

**3. Error handling:**
3 reintentos con espera exponencial (1min, 5min, 15min). Si falla los 3 → alerta a Slack. Si falla 3 días seguidos → circuit breaker + escalamiento al líder técnico. Tareas idempotentes para que los reintentos sean seguros.

**4. Métricas:**
- Tiempo total vs promedio (alerta si >2x)
- Registros procesados vs día anterior (alerta si <50%)
- Freshness: última actualización de Gold (alerta si >26 horas)
- Tasa de éxito semanal (objetivo: >95%)`,
  },

  // ── 22 ──
  {
    id: 22,
    level: 'SEMANA 5',
    title: 'Lección 22: Diseño de proyecto end-to-end',
    description: 'Diseñar un pipeline completo desde las fuentes hasta el dashboard, con documentación y defensa técnica.',
    theory: `# Diseño de proyecto end-to-end

## El pipeline completo

\`\`\`
[Fuentes] → [Ingesta] → [Bronze] → [Silver] → [Gold] → [Consumo]
\`\`\`

## Checklist del proyecto

### 1. Definir el problema
- ¿Qué pregunta queremos responder con datos?
- ¿Quién va a consumir los datos?
- ¿Con qué frecuencia se necesitan actualizaciones?

### 2. Identificar las fuentes
| Pregunta | Por qué importa |
|----------|----------------|
| ¿De dónde vienen? | Define la complejidad de ingesta |
| ¿En qué formato? | Define transformaciones necesarias |
| ¿Con qué frecuencia se actualizan? | Define el scheduling |
| ¿Qué volumen manejan? | Define la arquitectura |

### 3. Diseñar la arquitectura
- Capas: Bronze → Silver → Gold
- Ingesta: Full, Incremental, o CDC
- Modelo dimensional: Star o Snowflake
- SCDs: Type 1 o Type 2 según la dimensión
- Idempotencia en cada paso

### 4. Orquestar y monitorear
- DAG de tareas con dependencias
- Scheduling y error handling
- Métricas y alertas

### 5. Documentar
- Linaje de datos
- Diccionario de datos
- Decisiones de diseño

## Presentación y defensa técnica

### Estructura recomendada:
| Sección | Tiempo | Contenido |
|---------|--------|----------|
| Problema de negocio | 1 min | Qué se resolvió |
| Arquitectura | 3 min | Diagrama de flujo de datos |
| Decisiones clave | 5 min | Por qué se eligió cada enfoque |
| Demo | 5 min | Pipeline funcionando |
| Resultados | 3 min | Insights generados |
| Lecciones | 2 min | Qué salió bien y qué mejorarías |

### Preguntas típicas:
- "¿Por qué elegiste ese tipo de SCD?"
- "¿Qué pasa si la fuente envía datos duplicados?"
- "¿Cómo escalarías si el volumen se multiplica por 10?"
- "¿Cómo garantizás idempotencia?"`,
    exercise: {
      heading: 'Ejercicio final: Proyecto completo',
      instructions: [
        'Diseñá un pipeline completo para una **cadena de supermercados**:',
        '1. Identificá al menos 3 fuentes de datos con formato y frecuencia.',
        '2. Definí qué va en Bronze, Silver y Gold con transformaciones específicas.',
        '3. Diseñá el modelo dimensional: fact + al menos 3 dimensiones con grano.',
        '4. ¿Qué tipo de SCD usarías para dim_productos (que cambian de precio)?',
        '5. Dibujá el DAG del pipeline.',
        '6. ¿Qué métricas monitorearías?',
        '7. Escribí documentación básica para fact_ventas.',
      ],
      hint: 'Pensá como un Data Engineer entregando un proyecto real: cada decisión justificada.',
    },
    modelAnswer: `## Respuesta modelo

**1. Fuentes:**
| Fuente | Formato | Frecuencia |
|--------|---------|-----------|
| Sistema POS (punto de venta) | JSON vía API | Micro-batch cada 15 min |
| Catálogo de productos (ERP) | CSV | Diario |
| Datos de sucursales | Tabla relacional | Semanal |

**2. Capas:**
- **Bronze**: JSON de transacciones crudo, CSV tal cual, snapshot de sucursales. Todo con metadata de ingesta.
- **Silver**: Transacciones limpias (tipos corregidos, duplicados eliminados, montos validados >0). Productos normalizados. Sucursales estandarizadas.
- **Gold**: fact_ventas (línea de ticket), dim_clientes, dim_productos, dim_sucursales, dim_tiempo, kpi_ventas_diarias.

**3. Modelo dimensional:**

\`\`\`
    dim_productos
         │
dim_sucursales ── fact_ventas ── dim_tiempo
         │           │
    dim_clientes     └── nro_ticket (degenerada)
\`\`\`

**Grano**: una línea de ticket (producto × transacción)

**4. dim_productos → SCD Type 2** para los precios (historial de cambios de precio para análisis de rentabilidad).

**5. DAG:**
\`\`\`
ingestar_pos ────── limpiar_ventas ─────┐
ingestar_productos ─ limpiar_productos ─┤── crear_dims ── crear_fact ── crear_kpis ── notificar
ingestar_sucursales ─ limpiar_sucursales┘
\`\`\`

**6. Monitoreo:** Freshness (<2 hrs), volumen (>50% promedio), tiempo ejecución (<2x), calidad datos (>98%).

**7. Documentación fact_ventas:**
| Campo | Valor |
|-------|-------|
| Tabla | gold.fact_ventas |
| Grano | Línea de ticket |
| Fuente | silver.orders + dims |
| Frecuencia | Cada 15 min |
| Owner | Data Engineering |`,
  },
];
